import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { box, blob, latheZ, rodZ, dome, extrude, roundRect, ring, mergeAll } from './geometry.js';

/**
 * First-person arms.
 *
 * Two bones per arm, solved analytically from the hand (which is the thing the
 * animation drives — the hands are welded to the weapon, the elbows follow).
 * That is the same order of operations a real animator uses and it means the
 * hands can never slide off the grip.
 *
 * Anatomy is deliberate: a hand is 190 mm wrist-to-fingertip and 88 mm across
 * the knuckles, the fingers taper and *separate*, the knuckles are lumps, the
 * glove has a padded back, a palm patch, seams down the finger sides and a
 * velcro wrist strap, and the sleeve is a tapered tube with real fold rings and
 * a rolled cuff. That list is the difference between a hand and a grey sausage.
 *
 * Hand-local space: -Z along the fingers, +Y out of the back of the hand,
 * +X toward the thumb (a right hand; the left is mirrored).
 */

/**
 * Humerus and forearm+wrist lengths, in metres.
 *
 * A large adult is 300 / 272 mm, and those were the values here. They do not
 * work, and no viewmodel in the genre uses them: once the weapon is far enough
 * from the eye for the magazine and the muzzle to be in frame at all (300 mm —
 * see defs.js), the support hand is 515 mm downrange of a shoulder that has to
 * stay BEHIND the eye, and 572 mm of arm reaches that at 99.5% extension. The
 * two-bone solve then clamps, the elbow locks dead straight, and the arm reads
 * as a broomstick with the hand sliding off the handguard.
 *
 * The obvious alternative — blading the shoulder forward — was measured and is
 * worse: at shoulderZ -0.075 the 89 mm forearm sleeve crosses the frame
 * diagonally and occludes the barrel and muzzle outright, which is exactly what
 * the warning in viewmodel.js predicted.
 *
 * So the bones are cheated 10% long (330 / 300, reach 630 mm). That takes the
 * same target to 91% extension, which leaves a visible elbow bend, and it pushes
 * the elbow FURTHER out of frame rather than into it, because a longer chain
 * between fixed endpoints bends more.
 */
const L_UPPER = 0.33;
const L_FORE = 0.3;

/* -------------------------------------------------------------------------- */
/*  hand geometry                                                             */
/* -------------------------------------------------------------------------- */

/**
 * THE HAND IS BUILT AROUND SPHERICAL KNUCKLES. Read this before changing it.
 *
 * The card's complaint — "the hands still look like the old ones", "bend 90
 * degrees" — was twice misdiagnosed as a pose or a material problem and twice
 * "fixed" by retinting the same mesh. It is neither, and it is measurable.
 *
 * The old finger was three SEPARATE tapered lathes butted end to end. Each one
 * pinched to 35% of its radius at its tip and the next one opened with a flat
 * disc at 86% of its radius, so every joint carried a 6.4 mm-deep annular notch
 * with a hard disc rim on its far wall. Rotate that by the 60-77 degrees the
 * pose table asks of a metacarpophalangeal joint and the notch opens into a
 * wedge: walking the real transform chain to the dorsal surface of the middle
 * finger in the `wrap` pose measured the silhouette turning **96.4 degrees in a
 * single step** at the PIP, with 47 and 59 degree corners either side of it. A
 * 96-degree corner in the outline of a finger IS a right-angle bend, whatever
 * the joint rotations say it is.
 *
 * The fix is the one every hand rig uses: put a TRUE SPHERE of the joint's own
 * radius at each joint ORIGIN, and start and end each phalanx shaft just inside
 * the spheres at either end of it.
 *
 *   - A sphere centred on the pivot is invariant under the pivot's rotation, so
 *     the joint surface is identical at every bend angle. There is nothing to
 *     crack open, at any pose, ever.
 *   - The shaft meets the sphere at 0.965 of its radius, so the sphere stands
 *     ~0.35 mm proud over a ~5 mm band — which is what a knuckle is — and the
 *     outer silhouette through a bend is a circular arc of the knuckle radius.
 *     Smooth at any angle, by construction rather than by tuning.
 *   - Both ends of every shaft close with a cone strictly inside the
 *     neighbouring sphere, so no end cap is ever visible and there is no notch.
 *
 * FINGERS ARE ROUND, not squashed. The old code applied `scale(1, 0.88, 1)` to
 * every segment because bare fingers are wider than they are deep. A sphere
 * scaled that way stops being invariant under the joint's rotation and the
 * knuckle would swell and shrink as the hand closed. A padded tactical glove
 * finger — the thing in the reference photo — is essentially round anyway,
 * because the padding fills the section out. Roundness is what buys the
 * continuity. Keep it.
 */

/** Shaft radius where it meets a knuckle, as a fraction of that knuckle's radius. */
const KNUCKLE_PROUD = 0.965;

/**
 * The knuckle itself: a full sphere, centred on the joint origin.
 *
 * 16 x 8 is a 0.019 r sagitta, i.e. 0.2 mm on a 10 mm knuckle — about half a
 * pixel at the distance the support hand actually sits (0.35-0.45 m).
 */
function knuckleBall(r) {
  return dome(r, 16, 1); // cut = 1 -> a full sphere, not a cap
}

/**
 * One phalanx shaft, running along -Z from its own joint origin to the next.
 *
 * Authored in INCREASING axial order (tip first), because `latheZ` takes the
 * winding from the direction the profile runs and a decreasing profile builds
 * the tube inside out.
 *
 * @param {number} len   joint origin to joint origin
 * @param {number} r0    knuckle radius at this joint
 * @param {number} r1    knuckle radius at the next joint (or the fingertip)
 * @param {boolean} opts.tip  close with a rounded fingertip instead of a cone
 */
function phalanxShaft(len, r0, r1, opts = {}) {
  const tip = opts.tip ?? false;
  const K = KNUCKLE_PROUD;
  const rAt = (z) => r0 + (r1 - r0) * Math.min(1, Math.max(0, -z / len));
  // With a rounded tip the last `r1` of the authored length IS the tip, so the
  // finger does not grow by a radius when the cap is added.
  const Ls = tip ? len - r1 : len;
  const pts = [];
  if (tip) {
    for (let i = 5; i >= 1; i--) {
      const a = (i / 5) * (Math.PI / 2);
      pts.push([-Ls - r1 * Math.sin(a), r1 * Math.cos(a)]);
    }
    pts.push([-Ls, r1]);
  } else {
    // A cone that closes the shaft strictly INSIDE the next knuckle sphere:
    // the sphere's own radius at 0.45 r is 0.893 r, comfortably outside 0.86 r.
    pts.push(
      [-len - r1 * 0.45, 0],
      [-len - r1 * 0.45, r1 * 0.86],
      [-len - r1 * 0.12, r1 * 0.95],
      [-len, r1 * K]
    );
  }
  // Barrel. A gloved finger is fullest a third of the way along each phalanx;
  // a dead-straight cone between two knuckles reads as plumbing.
  pts.push(
    [-Ls * 0.72, rAt(-Ls * 0.72)],
    [-Ls * 0.42, rAt(-Ls * 0.42) * 1.008],
    [-Ls * 0.16, rAt(-Ls * 0.16)],
    [0, r0 * K],
    [r0 * 0.12, r0 * 0.95],
    [r0 * 0.45, r0 * 0.86],
    [r0 * 0.45, 0]
  );
  return latheZ(pts, 16);
}

/**
 * Quilted padding over the back of one phalanx.
 *
 * The reference photo's glove is quilted, not plated: the dorsal side of every
 * finger carries two or three stitched pillows with a flex gap between them,
 * and the gaps line up with the knuckles. The old code put ONE rigid slab per
 * segment spanning 78% of it, which at a 70-degree bend met its neighbour at a
 * hard chevron — half of the "stack of slabs" read, and the other half of the
 * 96-degree corner above.
 *
 * So: pillows over the MIDDLE 62% only, leaving a clear flex gap of bare shell
 * either side of every knuckle. That gap is what makes a glove look like it
 * bends.
 */
function quiltPad(len, r, cells = 2) {
  const parts = [];
  const span = len * 0.62;
  const gap = r * 0.22;
  const cell = (span - gap * (cells - 1)) / cells;
  for (let i = 0; i < cells; i++) {
    const g = blob(r * 1.5, r * 0.34, cell, Math.min(r * 0.17, cell * 0.45), 3);
    // Centre 0.88 r with a 0.17 r half-thickness puts the crown 1.05 r out —
    // 5% proud of a shaft at 1.0 r — while the corners at 0.71 r still bite into
    // the shell. MEASURED on a render at 0.5 r thick: the pillows stood a fifth
    // of a radius clear and read as separate bars bolted to the finger. Quilting
    // is a surface, not a pile of parts: it has to be barely proud.
    g.translate(0, r * 0.88, -len * 0.19 - (cell * 0.5 + i * (cell + gap)));
    parts.push(g);
  }
  return mergeAll(parts);
}

/** Textured grip patch on the PALMAR side of a phalanx. */
function gripPad(len, r) {
  const g = blob(r * 1.46, r * 0.34, len * 0.64, r * 0.15, 2);
  g.translate(0, -r * 0.9, -len * 0.5);
  return g;
}

/**
 * Stitched seam down a flank of a phalanx.
 *
 * A glove is sewn from a palm panel and a dorsal panel and the seam between
 * them runs down the side of every finger. It matters far out of proportion to
 * its size: at 40 px across the whole hand the four fingers merge into one
 * paddle, and the only thing that still separates them is a light line at each
 * boundary. A 1.5 mm strip at 1.85x the shell albedo (`glove_seam` in
 * materials.js) survives to about 3 px — one pixel of separation per finger.
 *
 * @param {number} sx  +1 thumb-side flank, -1 little-finger flank
 */
function phalanxSeam(len, r0, r1, sx) {
  const g = box(0.0015, (r0 + r1) * 0.3, len * 0.74, 0.0003, 1);
  // Fingers are round now, so the side wall sits at r, not at 0.88 r.
  g.translate(sx * (r0 + r1) * 0.5, 0, -len * 0.5);
  return g;
}

/**
 * Build one finger as three nested joints.
 *
 * Every part is rigid with respect to exactly one joint, which is what lets
 * `Arm.skin()` collapse the whole hand to one skinned mesh per material without
 * changing a single pixel. Do not add a part that spans two joints.
 *
 * @returns {{root: THREE.Object3D, joints: THREE.Object3D[]}}
 */
function buildFinger(materials, spec) {
  const { lengths, radii, curl, seamSide, knuckleScale } = spec;
  const root = new THREE.Object3D();
  const joints = [];
  let parent = root;
  for (let i = 0; i < 3; i++) {
    const j = new THREE.Object3D();
    j.rotation.x = -(curl?.[i] ?? 0);
    parent.add(j);

    const r0 = radii[i];
    const r1 = radii[i + 1];
    // The metacarpophalangeal knuckle is the big one — it is the lump you see
    // on the back of a closed fist — so it gets a little extra radius.
    const kr = r0 * (i === 0 ? (knuckleScale ?? 1.08) : 1);
    const shell = mergeAll([knuckleBall(kr), phalanxShaft(lengths[i], r0, r1, { tip: i === 2 })]);
    const mesh = new THREE.Mesh(shell, materials.glove);
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    j.add(mesh);

    // Quilted dorsal padding: two pillows on the long proximal phalanx, one on
    // the middle, none on the distal (it is 22 mm long and a pillow there is
    // sub-pixel; the fingertip gets a grip patch instead).
    if (i < 2) {
      j.add(new THREE.Mesh(quiltPad(lengths[i], (r0 + r1) * 0.5, i === 0 ? 2 : 1), materials.pad));
    }
    // Palmar grip patch on every phalanx — this is what the fingers actually
    // present to the camera when they are wrapped round a handguard.
    j.add(new THREE.Mesh(gripPad(lengths[i], (r0 + r1) * 0.5), materials.pad));

    // Seams down BOTH flanks on the two longer phalanges. One seam per finger
    // leaves three boundaries out of five unmarked.
    if (i < 2) {
      const seams = mergeAll(
        (seamSide ?? 0) === 0
          ? [phalanxSeam(lengths[i], r0, r1, 1), phalanxSeam(lengths[i], r0, r1, -1)]
          : [phalanxSeam(lengths[i], r0, r1, seamSide)]
      );
      j.add(new THREE.Mesh(seams, materials.seam ?? materials.glove));
    }

    const next = new THREE.Object3D();
    next.position.z = -lengths[i];
    j.add(next);
    parent = next;
    joints.push(j);
  }
  return { root, joints };
}

/**
 * Cup the palmar surface.
 *
 * A flat palm is the other half of "it reads as a brick": a real palm is a
 * shallow dish between the thenar and hypothenar eminences, and on a C-clamp
 * the camera looks straight into it. Applied once at build time, so it costs
 * nothing at runtime and the hand stays one mesh per material.
 */
function cupPalm(geo, w, h, palmLen, depth, oy = 0, oz = 0) {
  const pos = geo.getAttribute('position');
  if (!pos) return geo;
  const smooth = (x) => x * x * (3 - 2 * x);
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i) + oy;
    if (y >= 0) continue; // dorsal half is untouched
    const x = pos.getX(i);
    const z = pos.getZ(i) + oz;
    const u = THREE.MathUtils.clamp(1 - Math.abs(x) / (w * 0.46), 0, 1);
    const t = THREE.MathUtils.clamp(1 - Math.abs(z + palmLen * 0.56) / (palmLen * 0.44), 0, 1);
    // Only the palmar SURFACE moves; vertices near the mid-plane stay put, so
    // the dish has a soft rim instead of a step.
    const d = THREE.MathUtils.clamp(-y / (h * 0.42), 0, 1);
    pos.setY(i, pos.getY(i) + depth * smooth(u) * smooth(t) * d);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/**
 * Glove: a heel, a hinged metacarpal arch, knuckle armour, quilted panels, and
 * an elastic cuff with a wrist strap and a logo disc.
 *
 * THE PALM HAS A JOINT IN IT. Read this before flattening it again.
 *
 * A 98 mm palm is 98 mm of arc on whatever it grips, and a handguard is 42-54
 * mm across. MEASURED on the shipped rig: with the palm a rigid slab the only
 * thing that could touch the tube was the metacarpal row — 1.7-3.8 mm off —
 * while the heel hung 27-29 mm out in mid-air, and the build-time fingertip
 * solve then had to reach the tube from a hand that was not on it. That is the
 * "palm floats, fingers interpenetrate" failure, and no amount of posing fixes
 * it, because the shape is wrong: the geometry of a flat 98 mm plane and a 27
 * mm cylinder admits exactly one line of contact.
 *
 * A real hand does not have that problem, because a real palm is not flat. The
 * fourth and fifth metacarpals hinge at their carpometacarpal joints and the
 * palm folds into a longitudinal arch around whatever it holds; that fold is
 * what lets a 98 mm palm wrap a 45 mm tube. So the glove gets the same joint:
 *
 *   root (bone 0)   the heel, thenar and hypothenar pads, cuff, strap, disc.
 *   arch (bone 1)   the metacarpal half of the palm, the knuckle guard, the
 *                   quilted dorsal panel, and every finger and the thumb.
 *
 * Baking the arch into the mesh instead was tried on paper and rejected: the
 * same hand plays `open` (magazine grab), `pinch` and `cup`, and a palm
 * permanently folded 60 degrees is a banana in all three. As a joint it is
 * free — `Arm.skin()` already collapses one rigid part per bone, so this costs
 * one more bone and no draw calls — and `fitToCylinder` can SOLVE it per
 * weapon, which is what actually seats the heel.
 *
 * CARPAL ROLL. The split is hidden the same way the knuckles are: a cylinder
 * on the hinge axis, of the palm's own half-depth, carried by the root. A
 * cylinder about the axis it rotates on is invariant under that rotation, so
 * the two halves of the palm are joined by a continuous surface at every arch
 * angle, with nothing to crack open.
 *
 * COVERAGE BUDGET: armour plus padding on the dorsum must not exceed 55% of the
 * back of the hand. An earlier version ran to ~57% at a single height with a
 * single material and the whole dorsum merged into one continuous shelf — the
 * "stack of grey slabs" read, which no amount of retinting fixes, because what
 * the eye objects to is that there is no soft glove left on the back of the
 * hand. The guard below is 23% and the metacarpal quilting 12%: 35%, with a
 * clear band of bare shell between them.
 *
 * @returns {{root: THREE.Object3D, arch: THREE.Object3D}}
 */
function buildGlove(materials, opts = {}) {
  const scale = opts.scale ?? 1;
  const w = 0.088 * scale; // across the knuckles
  const h = 0.032 * scale; // through the heel of the hand
  const palmLen = 0.098 * scale;
  const root = new THREE.Object3D();

  /** Carpometacarpal hinge: the base of the metacarpals, palmar of centre. */
  const archZ = -0.031 * scale;
  const archY = -0.004 * scale;
  const arch = new THREE.Object3D();
  arch.name = 'palm-arch';
  arch.position.set(0, archY, archZ);
  root.add(arch);
  /** Convert a hand-space Z into the arch's own space. */
  const az = (z) => z - archZ;
  const ay = (y) => y - archY;

  /**
   * The palm tapers in DEPTH toward the knuckles — 32 mm through the heel, 21
   * mm through the metacarpal heads — and its front end drops to meet the
   * finger row, whose joint origins sit 6 mm palmar of the hand's centre plane.
   * Without that the knuckle spheres are buried inside a slab and the back of
   * the hand ends in a flat wall.
   */
  const heelShell = [];
  const palmRear = blob(w * 0.84, h * 0.97, palmLen * 0.4, 0.012 * scale, 3);
  palmRear.translate(0, 0, -palmLen * 0.19);
  heelShell.push(palmRear);
  const heel = blob(w * 0.92, h * 0.88, 0.03 * scale, 0.012 * scale, 3);
  heel.translate(0, -h * 0.02, -0.012 * scale);
  heelShell.push(heel);
  // Thenar (thumb muscle) and hypothenar: the two pads that actually wrap the
  // SIDES of a tube when the heel is on it, which is what keeps the grip from
  // reading as a plank laid on a pipe.
  const thenar = blob(w * 0.4, h * 0.88, palmLen * 0.34, 0.014 * scale, 3);
  thenar.translate(w * 0.3, -h * 0.07, -palmLen * 0.2);
  heelShell.push(thenar);
  const hypo = blob(w * 0.28, h * 0.74, palmLen * 0.3, 0.012 * scale, 3);
  hypo.translate(-w * 0.35, -h * 0.1, -palmLen * 0.21);
  heelShell.push(hypo);
  const heelGeo = mergeAll(heelShell);
  cupPalm(heelGeo, w, h, palmLen, 0.0036 * scale);
  root.add(new THREE.Mesh(heelGeo, materials.glove));

  // The carpal roll: invariant under the arch rotation, so the two halves of
  // the palm never part. Authored along Z then laid on the hinge axis (+X).
  const rollR = h * 0.48;
  const roll = latheZ(
    [
      [-w * 0.42, 0],
      [-w * 0.42, rollR * 0.72],
      [-w * 0.4, rollR],
      [w * 0.4, rollR],
      [w * 0.42, rollR * 0.72],
      [w * 0.42, 0],
    ],
    16
  );
  roll.rotateY(Math.PI / 2);
  roll.translate(0, archY, archZ);
  root.add(new THREE.Mesh(roll, materials.glove));

  /* ---- the arch: metacarpals, knuckles, fingers ------------------------- */

  const archShell = [];
  const mid = blob(w * 0.97, h * 0.86, palmLen * 0.34, 0.012 * scale, 3);
  mid.translate(0, ay(-h * 0.05), az(-palmLen * 0.47));
  archShell.push(mid);
  const front = blob(w, h * 0.64, palmLen * 0.4, 0.011 * scale, 3);
  front.translate(0, ay(-h * 0.16), az(-palmLen * 0.77));
  archShell.push(front);
  const archGeo = mergeAll(archShell);
  cupPalm(archGeo, w, h, palmLen, 0.0046 * scale, archY, archZ);
  arch.add(new THREE.Mesh(archGeo, materials.glove));

  /**
   * Knuckle armour: ONE moulded guard with four lobes and three flex gaps, not
   * four detached caps and not one slab. The gaps are the entire read — they
   * give the silhouette four lobes instead of a rectangle — and the thin web
   * below them is what says the lobes belong to one piece of TPR.
   *
   * PROUD BY 3.5 mm, measured off the shell, and that is a requirement rather
   * than a taste: at the 0.9 mm it used to stand it existed only as a tint, so
   * on a flat-shaded render — and on any frame where the glove is in shadow,
   * which is most of them — the back of the hand was a blank lozenge. A guard
   * has to be in the SILHOUETTE.
   */
  const pads = [];
  for (let i = 0; i < 4; i++) {
    const x = w * (0.335 - i * 0.223);
    const drop = Math.abs(i - 1.5) > 1 ? h * 0.05 : 0;
    const lobe = blob(w * 0.175, h * 0.42, palmLen * 0.3, 0.005 * scale, 3);
    lobe.translate(x, ay(h * 0.36 - drop), az(-palmLen * 0.84));
    pads.push(lobe);
  }
  const webPlate = blob(w * 0.82, h * 0.16, palmLen * 0.12, 0.003 * scale, 2);
  webPlate.translate(0, ay(h * 0.3), az(-palmLen * 0.76));
  pads.push(webPlate);
  /**
   * Quilted metacarpal panel: three stitched pillows down the back of the hand,
   * with a clear gap between them and the knuckle guard. The photo's glove has
   * exactly this — the back is padded cloth, not plate.
   */
  for (let i = 0; i < 3; i++) {
    const p = blob(w * 0.56, h * 0.22, palmLen * 0.1, 0.004 * scale, 2);
    p.translate(0, ay(h * 0.4 - i * h * 0.02), az(-palmLen * (0.58 - i * 0.12)));
    pads.push(p);
  }
  // Palm grip patch over the metacarpal heads — the part of the palm that is
  // actually against the weapon.
  const patch = blob(w * 0.8, h * 0.17, palmLen * 0.44, 0.006 * scale, 3);
  patch.translate(0, ay(-h * 0.46), az(-palmLen * 0.6));
  pads.push(patch);
  arch.add(new THREE.Mesh(mergeAll(pads), materials.pad));

  const heelPads = [];
  const heelPad = blob(w * 0.74, h * 0.18, 0.034 * scale, 0.005 * scale, 3);
  heelPad.translate(0, -h * 0.47, -palmLen * 0.14);
  heelPads.push(heelPad);
  root.add(new THREE.Mesh(mergeAll(heelPads), materials.pad));

  // Seams down the sides of the hand, continuing the finger seams. Split at the
  // hinge so each piece belongs to exactly one bone.
  const heelSeams = [];
  const archSeams = [];
  for (const sx of [-1, 1]) {
    const a = box(0.0018 * scale, h * 0.5, palmLen * 0.3, 0.0004, 1);
    a.translate(sx * w * 0.47, -h * 0.03, -palmLen * 0.17);
    heelSeams.push(a);
    const b = box(0.0018 * scale, h * 0.46, palmLen * 0.52, 0.0004, 1);
    b.translate(sx * w * 0.5, ay(-h * 0.06), az(-palmLen * 0.62));
    archSeams.push(b);
  }
  root.add(new THREE.Mesh(mergeAll(heelSeams), materials.seam ?? materials.pad));
  arch.add(new THREE.Mesh(mergeAll(archSeams), materials.seam ?? materials.pad));

  return { root, arch };
}

/**
 * Thumb: two segments on the +X side, angled across the grip, built from the
 * same knuckle spheres as the fingers.
 *
 * THE PROXIMAL SEGMENT IS THE METACARPAL AS WELL AS THE PROXIMAL PHALANX, and
 * that is why it is 50 mm rather than 38.
 *
 * MEASURED: with a 38 + 30 mm thumb the C-clamp solve (Arm.fitToCylinder) left
 * the tip 13.2 mm clear of the handguard no matter how the base was aimed —
 * scanning abduction alone, then abduction AND rotation in a 21 x 15 grid, moved
 * it by 1 mm. It is not an aiming problem, it is a reach problem: the thumb root
 * sits at the heel of the palm, the palm on a C-clamp stands 29 mm off a 54 mm
 * tube (unavoidable — a 98 mm palm tangent to a 27 mm radius diverges), and 68 mm
 * of thumb simply does not get there.
 *
 * A real hand does not have that problem because the thumb column starts at the
 * CARPOMETACARPAL joint, deep in the wrist, and the visible thumb from the web to
 * the tip is 75-85 mm. This rig has no metacarpal segment at all, so the proximal
 * one absorbs it: 50 + 32 = 82 mm, which reaches with 10 mm of flexion in hand.
 */
function buildThumb(materials, scale = 1, spec = THUMB) {
  const root = new THREE.Object3D();
  const l0 = spec.l0 * scale;
  const l1 = spec.l1 * scale;
  const r0 = spec.r0 * scale;
  const r1 = spec.r1 * scale;
  const r2 = spec.r2 * scale;

  const j1 = new THREE.Object3D();
  root.add(j1);
  j1.add(
    new THREE.Mesh(
      mergeAll([knuckleBall(r0 * 1.0), phalanxShaft(l0, r0, r1)]),
      materials.glove
    )
  );
  j1.add(new THREE.Mesh(quiltPad(l0, (r0 + r1) * 0.5, 2), materials.pad));
  j1.add(new THREE.Mesh(gripPad(l0, (r0 + r1) * 0.5), materials.pad));
  j1.add(
    new THREE.Mesh(
      mergeAll([phalanxSeam(l0, r0, r1, 1), phalanxSeam(l0, r0, r1, -1)]),
      materials.seam ?? materials.glove
    )
  );

  const j2 = new THREE.Object3D();
  j2.position.z = -l0;
  j1.add(j2);
  j2.add(
    new THREE.Mesh(
      mergeAll([knuckleBall(r1), phalanxShaft(l1, r1, r2, { tip: true })]),
      materials.glove
    )
  );
  j2.add(new THREE.Mesh(gripPad(l1, (r1 + r2) * 0.5), materials.pad));
  j2.add(new THREE.Mesh(quiltPad(l1, (r1 + r2) * 0.5, 1), materials.pad));
  return { root, joints: [j1, j2] };
}

/** Thumb dimensions, shared by the mesh and the contact solve. */
const THUMB = { l0: 0.05, l1: 0.032, r0: 0.0115, r1: 0.0102, r2: 0.0078 };

/**
 * THE GLOVE'S CUFF BELONGS TO THE FOREARM, not to the hand.
 *
 * dc709e6 deleted the cuff and the wrist strap outright, and its render says
 * exactly why (shots/arms-2026-10-14/rifle-wrist-flap-before.png): two brown
 * flaps standing clear of the green sleeve at the wrist, obviously detached
 * from the arm they were supposed to be on.
 *
 * The cause is not that they were open tubes, although they were. It is that
 * they were authored in HAND space. A cuff 24 mm long on a wrist that breaks
 * 23 degrees swings its far rim 9 mm off the forearm's axis before you add any
 * hem at all, so one side lifts away from the arm and the other buries in it —
 * and there is no radius at which that stops happening, because the hand and
 * the forearm are simply not parallel. Rebuilding them as closed solids, which
 * this branch had already done, fixes the hollow but not the flap.
 *
 * So they move onto the forearm bone, where they are aligned with the arm by
 * construction and cannot flap at any wrist angle. This is also what a glove
 * actually does: the elastic grips the forearm and the hand rotates inside it.
 * The glove's own wrist end is narrower than the collar and tucks into it.
 *
 * Authored along +Z (back toward the elbow) from the wrist.
 */
function buildWristCuff(materials, scale = 1) {
  /**
   * Elliptical, because the wrist it grips is: card 9b47fa2f's forearm is
   * 67 x 42 mm at the wrist (buildForearm), so the cuff is 68 x 44 mm and
   * hugs it (tools/armfit.mjs). It was a 54 x 47 mm near-circle around a
   * near-circular 54 x 46 mm wrist. The strap is lower (1.1 R, was 1.2) so it
   * stands about 3 mm proud, which reads as a strap, not a collar.
   */
  const R = 0.0305 * scale;
  const SX = 1.12;
  const SY = 0.72;
  const parts = [];
  /**
   * A CLOSED solid: both ends of the lathe come back to the axis, so there is
   * no interior for the camera to find. The rear face is a genuine annulus —
   * everything inside the forearm's own radius is buried in it and only the
   * hem ring shows, which is what the elastic hem of a glove looks like.
   */
  const cuff = latheZ(
    [
      [-0.005 * scale, 0],
      [-0.005 * scale, R * 0.84],
      [0.001 * scale, R * 0.96],
      [0.009 * scale, R],
      [0.018 * scale, R * 0.985],
      // A long, soft rear taper rather than a step, so the hem rolls onto the
      // forearm instead of presenting a flat ring to a camera behind the wrist.
      [0.023 * scale, R * 0.93],
      [0.027 * scale, R * 0.82],
      [0.029 * scale, R * 0.6],
      [0.0295 * scale, 0],
    ],
    20
  );
  cuff.scale(SX, SY, 1);
  const group = new THREE.Object3D();
  group.name = 'wrist-cuff';
  group.add(new THREE.Mesh(cuff, materials.glove));

  /**
   * The strap stands ~3 mm proud of the cuff and the disc ~4 mm proud of the
   * forearm. Both are floors, not preferences: under about 2 mm neither appears
   * in a silhouette, so on a back-lit frame — which, on the evidence of the
   * engine renders, is most of them — the cuff is a plain dark band and the two
   * details the reference photo is most legible for are simply not there.
   */
  const strap = latheZ(
    [
      [0, R * 0.99],
      [0.0026 * scale, R * 1.1],
      [0.0104 * scale, R * 1.1],
      [0.013 * scale, R * 0.99],
    ],
    20
  );
  strap.scale(SX, SY, 1);
  strap.translate(0, 0, 0.004 * scale);
  parts.push(strap);
  const disc = latheZ(
    [
      [0, 0],
      [0, R * 0.34],
      [0.003 * scale, R * 0.38],
      [0.0072 * scale, R * 0.36],
      [0.008 * scale, 0],
    ],
    18
  );
  disc.rotateX(-Math.PI / 2);
  // Seated a little into the strap's crown, so it reads as stitched on.
  disc.translate(0, R * SY * 1.05, 0.0108 * scale);
  parts.push(disc);
  group.add(new THREE.Mesh(mergeAll(parts), materials.pad));
  return group;
}

/**
 * Tapered sleeve with fold rings, an elbow pad and a rolled cuff.
 * Both ends are CLOSED — an open lathe reads as a length of pipe, which is
 * exactly the "grey sausage" failure this rig has to avoid.
 */
function buildSleeve(material, len, r0, r1, opts = {}) {
  const parts = [];
  /**
   * SEGMENT COUNT. The support forearm's closest approach to the eye is ~0.38 m
   * and it is ~120 px wide, so a 20-gon puts a facet sagitta of 0.7 px on the
   * silhouette — countable, and countable facets are exactly what the critique
   * measured. 32 takes it to 0.28 px, under the AA threshold.
   */
  const SEG = 32;
  /**
   * The shell profile is no longer a smooth cone. A sleeved forearm has three
   * things a cone does not: the fabric is loose so it bells slightly behind the
   * elbow, it is pulled tight over the muscle belly a third of the way down, and
   * it bunches again at the cuff. Those three inflections are what make the
   * silhouette read as cloth over a limb rather than as pipe.
   */
  const shell = latheZ(
    [
      [0, 0],
      [0, r0 * 0.55],
      [-0.004, r0 * 0.82],
      [-0.006, r0 * 0.98],
      [0.004, r0],
      [len * 0.16, r0 * 1.03],
      [len * 0.34, r0 * 0.9],
      [len * 0.52, (r0 + r1) * 0.5],
      [len * 0.72, r1 * 1.1],
      [len - 0.016, r1 * 1.0],
      [len - 0.005, r1 * 1.07],
      [len, r1 * 0.98],
      [len + 0.003, r1 * 0.8],
      [len + 0.004, 0],
    ],
    SEG
  );
  parts.push(shell);
  // Joint mass at the far end so the two bones read as one limb.
  const joint = latheZ(
    [
      [len - r1 * 1.1, 0],
      [len - r1 * 0.9, r1 * 0.75],
      [len - r1 * 0.2, r1 * 1.04],
      [len + r1 * 0.5, r1 * 0.9],
      [len + r1 * 0.8, r1 * 0.4],
      [len + r1 * 0.85, 0],
    ],
    20
  );
  joint.scale(1, 0.94, 1);
  parts.push(joint);
  /**
   * Fold rings. These are not decoration: they are the only concave creases on
   * the whole limb, and the curvature mask bake (Arm.bakeSurfaceMasks) turns
   * every one of them into a grime line with a dust-rubbed crown either side.
   * That is what puts texture on a surface whose albedo is 0.013 linear.
   *
   * Ellipticity and a per-fold radius jitter matter as much as the count: eight
   * identical circular rings equally spaced read as a hose, which is the failure
   * this is here to avoid.
   */
  const folds = opts.folds ?? 3;
  for (let i = 0; i < folds; i++) {
    const t = 0.14 + (i / Math.max(1, folds - 1)) * 0.7;
    // deterministic wobble, so captures stay byte-identical
    const j = Math.sin(i * 2.399 + 0.7) * 0.5 + Math.sin(i * 5.13) * 0.25;
    const r = (r0 + (r1 - r0) * t) * (1 + j * 0.06);
    const f = ring(r * 0.985, r * (0.085 + j * 0.03), 24, 6);
    f.rotateX(Math.PI / 2);
    f.rotateY(j * 0.12);
    f.scale(1, 0.93, 1);
    f.translate(0, 0, len * t + j * 0.004);
    parts.push(f);
  }
  /**
   * Two longitudinal wrinkle ridges down the inboard and outboard flanks. A
   * tube's silhouette is a straight line; a sleeve's is not, and these are the
   * cheapest thing that breaks it. They sit just proud of the shell so they
   * catch the key on their crown and shade the shell beside them.
   */
  const ridge = opts.ridges ?? 1;
  for (const sx of ridge > 0 ? [-1, 1] : []) {
    const w = latheZ(
      [
        [len * 0.2, 0],
        [len * 0.3, r0 * 0.16 * ridge],
        [len * 0.55, r0 * 0.2 * ridge],
        [len * 0.78, r0 * 0.13 * ridge],
        [len * 0.86, 0],
      ],
      10
    );
    w.scale(1, 0.5, 1);
    w.rotateZ(sx * 0.4);
    w.translate(sx * (r0 + r1) * 0.46, -(r0 + r1) * 0.1, 0);
    parts.push(w);
  }
  if (opts.elbowPad) {
    const pad = blob(r0 * 1.5, r0 * 0.6, len * 0.3, r0 * 0.3, 3);
    pad.translate(0, r0 * 0.75, len * 0.12);
    parts.push(pad);
  }
  if (opts.cuff) {
    // Rolled, stitched cuff: two proud bands with a seam channel between them,
    // which is what a combat-shirt cuff actually looks like and gives the wrist
    // a hard terminator so the sleeve does not appear to melt into the glove.
    const cuff = latheZ(
      [
        [len - 0.032, r1 * 1.02],
        [len - 0.029, r1 * 1.17],
        [len - 0.019, r1 * 1.16],
        [len - 0.016, r1 * 1.08],
        [len - 0.012, r1 * 1.08],
        [len - 0.009, r1 * 1.18],
        [len - 0.003, r1 * 1.17],
        [len, r1 * 1.02],
      ],
      SEG
    );
    parts.push(cuff);
  }
  const g = mergeAll(parts);
  shapeSleeveAnatomy(g, len, opts);
  g.rotateY(Math.PI); // extend along -Z, like the bones
  return new THREE.Mesh(g, material);
}

/**
 * Give a sleeve the cross-section of an arm rather than a turned tube.
 *
 * `latheZ` is useful for the cloth folds above, but its perfectly round section
 * is an immediate procedural tell at first-person distance.  Human limbs are
 * wider side-to-side than front-to-back; the forearm also has a fuller mobile
 * muscle mass near the elbow and a hard ulnar side that survives under cloth.
 * We apply that shape once while building the geometry, then recompute normals.
 * It therefore costs nothing during gameplay and remains one mesh/material.
 */
function shapeSleeveAnatomy(geo, len, opts = {}) {
  const pos = geo.getAttribute('position');
  if (!pos) return geo;
  const forearm = opts.kind === 'forearm';
  const side = opts.side ?? 1;
  for (let i = 0; i < pos.count; i++) {
    const z = pos.getZ(i);
    // `buildSleeve` is authored along +Z before the final mirror.
    const t = THREE.MathUtils.clamp(z / Math.max(len, 1e-4), 0, 1);
    const x = pos.getX(i);
    const y = pos.getY(i);
    const r = Math.hypot(x, y);
    if (r < 1e-5) continue; // closed end-cap centre
    const a = Math.atan2(y, x);
    // Side-to-side breadth is greater than depth.  The lower arm flattens as
    // it reaches the wrist, while the upper sleeve keeps a rounder deltoid end.
    const breadth = forearm ? 1.11 - t * 0.055 : 1.075 - t * 0.025;
    const depth = forearm ? 0.86 + t * 0.045 : 0.91 + t * 0.015;
    // The ulnar ridge and flexor mass are deliberately subtle: together they
    // break the circle under a grazing light without turning fabric into skin.
    const ulnar = Math.cos(a - side * 1.15) * (forearm ? 0.055 : 0.026);
    const flexor = Math.sin(a + 0.62) * Math.sin(Math.PI * t) * (forearm ? 0.038 : 0.018);
    const contour = 1 + ulnar + flexor;
    pos.setXYZ(i, x * breadth * contour, y * depth * contour, z);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/**
 * Bare forearm, built as a forearm rather than as a sleeve with the folds off.
 *
 * Card 9b47fa2f: "more thick and natural ... natural curves and natural
 * smoothness". The previous skin was `buildSleeve` with folds disabled: an
 * 80 mm cone with a cloth profile (a bell behind the elbow, a pinch a third of
 * the way down) that is right for fabric and wrong for a limb, so it read as a
 * thin straight tube.
 *
 * A real forearm is not a cone. Its girth is all in the top third, where the
 * brachioradialis and wrist extensors sit on the thumb side and the flexor mass
 * on the palm side; it then tapers along a convex-then-concave curve into a
 * distal third that is mostly tendon and noticeably FLATTER than it is wide;
 * and the wrist is an ellipse about 1.6:1. Those three facts are what this
 * profile encodes, sized for a big, muscular adult: ~120 x 105 mm at the belly
 * (a 35 cm forearm) and ~67 x 42 mm at the wrist.
 *
 * The wrist DEPTH is deliberately less than the old 46 mm, not more: see the
 * note on `this.fore` — a forearm deeper than the palm pushes the glove cuff
 * into the handguard. All the extra width at the wrist goes sideways.
 *
 * Authored along +Z (elbow -> wrist) like buildSleeve, mirrored at the end.
 * `side` puts the muscle masses on the thumb side of the correct hand: the bone
 * is rolled with the back of the hand (+Y dorsal), and in authoring space,
 * before the final rotateY(PI), the thumb side is +X on the right arm.
 */
function buildForearm(material, len, scale = 1, side = 1) {
  const SEG = 48;
  // Mean section radius along the bone, metres, as (t, r). Smoothed by a
  // monotone cubic so the silhouette has no kinks to catch the light.
  const KEYS = [
    [0.0, 0.05],
    [0.08, 0.0535],
    [0.22, 0.0555],
    [0.4, 0.051],
    [0.58, 0.0435],
    [0.75, 0.0362],
    [0.88, 0.0313],
    [1.0, 0.0295],
  ].map(([t, r]) => [t, r * scale]);
  const rAt = monotoneCubic(KEYS);
  const STATIONS = 40;
  const prof = [
    [-0.004 * scale, 0],
    [-0.004 * scale, rAt(0) * 0.7],
  ];
  for (let i = 0; i <= STATIONS; i++) {
    const t = i / STATIONS;
    // The last 8 mm go into the glove; ease them in under the cuff's front lip.
    const tuck = 1 - 0.1 * smooth01(THREE.MathUtils.clamp((len * t - (len - 0.008 * scale)) / (0.008 * scale), 0, 1));
    prof.push([len * t, rAt(t) * tuck]);
  }
  prof.push([len + 0.002 * scale, rAt(1) * 0.85], [len + 0.004 * scale, 0]);
  const g = latheZ(prof, SEG);

  // Directions in the section, as angles. Radial = thumb side.
  const radial = side > 0 ? 0 : Math.PI;
  const toward = (from, to, f) => from + wrapAngle(to - from) * f;
  const DORSAL = Math.PI / 2;
  const VOLAR = -Math.PI / 2;
  const ulnar = radial + Math.PI;
  const extensorA = toward(radial, DORSAL, 0.45); // brachioradialis + ECRL
  const flexorA = toward(VOLAR, ulnar, 0.3); // FCU/FDS, palm side, little-finger leaning
  const ulnaA = toward(DORSAL, ulnar, 0.6); // subcutaneous border of the ulna

  const pos = g.getAttribute('position');
  for (let i = 0; i < pos.count; i++) {
    const z = pos.getZ(i);
    const x = pos.getX(i);
    const y = pos.getY(i);
    const r = Math.hypot(x, y);
    if (r < 1e-6) continue;
    const t = THREE.MathUtils.clamp(z / len, 0, 1);
    const a = Math.atan2(y, x);
    const breadth = 1.04 + 0.08 * t;
    const depth = 0.95 - 0.25 * smooth01(t);
    const contour =
      1 +
      0.065 * lobe(a, extensorA, 0.75) * window01(t, 0.02, 0.2, 0.5) +
      0.05 * lobe(a, flexorA, 0.85) * window01(t, 0.0, 0.18, 0.62) -
      0.03 * lobe(a, ulnaA, 0.35) * window01(t, 0.15, 0.4, 0.85) +
      // Tendons on the palm side of the distal third: a faint double ridge.
      0.012 * (lobe(a, VOLAR - 0.35, 0.18) + lobe(a, VOLAR + 0.35, 0.18)) * window01(t, 0.55, 0.78, 0.95);
    pos.setXYZ(i, x * breadth * contour, y * depth * contour, z);
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  weldSeamNormals(g);
  g.rotateY(Math.PI);
  return new THREE.Mesh(g, material);
}

function wrapAngle(a) {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

/** Gaussian lobe around angle `c`, `w` radians wide. */
function lobe(a, c, w) {
  const d = wrapAngle(a - c) / w;
  return Math.exp(-d * d);
}

function smooth01(t) {
  return t * t * (3 - 2 * t);
}

/** 0 outside [a, c], rising smoothly to 1 at b. */
function window01(t, a, b, c) {
  if (t <= a || t >= c) return 0;
  return t < b ? smooth01((t - a) / (b - a)) : smooth01((c - t) / (c - b));
}

/** Fritsch-Carlson monotone cubic through sorted (x, y) keys. */
function monotoneCubic(keys) {
  const n = keys.length;
  const xs = keys.map((k) => k[0]);
  const ys = keys.map((k) => k[1]);
  const d = [];
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));
  const m = new Array(n);
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) m[i] = d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) {
      m[i] = m[i + 1] = 0;
      continue;
    }
    const a = m[i] / d[i];
    const b = m[i + 1] / d[i];
    const s = a * a + b * b;
    if (s > 9) {
      const k = 3 / Math.sqrt(s);
      m[i] = k * a * d[i];
      m[i + 1] = k * b * d[i];
    }
  }
  return (x) => {
    let i = 0;
    while (i < n - 2 && x > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i];
    const u = THREE.MathUtils.clamp((x - xs[i]) / h, 0, 1);
    const u2 = u * u;
    const u3 = u2 * u;
    return (
      (2 * u3 - 3 * u2 + 1) * ys[i] + (u3 - 2 * u2 + u) * h * m[i] + (-2 * u3 + 3 * u2) * ys[i + 1] + (u3 - u2) * h * m[i + 1]
    );
  };
}

/**
 * A lathe duplicates its seam column (for the UVs), so computeVertexNormals
 * gives the two copies different normals and a smooth limb grows a hard crease
 * down one side. Average normals across coincident positions.
 */
function weldSeamNormals(geo) {
  const pos = geo.getAttribute('position');
  const nrm = geo.getAttribute('normal');
  const key = (i) => `${Math.round(pos.getX(i) * 1e6)},${Math.round(pos.getY(i) * 1e6)},${Math.round(pos.getZ(i) * 1e6)}`;
  const groups = new Map();
  for (let i = 0; i < pos.count; i++) {
    const k = key(i);
    const g = groups.get(k);
    if (g) g.push(i);
    else groups.set(k, [i]);
  }
  const n = new THREE.Vector3();
  for (const ids of groups.values()) {
    if (ids.length < 2) continue;
    n.set(0, 0, 0);
    for (const i of ids) n.set(n.x + nrm.getX(i), n.y + nrm.getY(i), n.z + nrm.getZ(i));
    if (n.lengthSq() < 1e-12) continue;
    n.normalize();
    for (const i of ids) nrm.setXYZ(i, n.x, n.y, n.z);
  }
  nrm.needsUpdate = true;
  return geo;
}

/* -------------------------------------------------------------------------- */
/*  arm rig                                                                   */
/* -------------------------------------------------------------------------- */

const _t = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _perp = new THREE.Vector3();
const _elbow = new THREE.Vector3();
const _up = new THREE.Vector3();
const _pole = new THREE.Vector3();
const _u1 = new THREE.Vector3();
const _u2 = new THREE.Vector3();
const _cand = new THREE.Vector3();
const _handZ = new THREE.Vector3();
const _foreDir = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _fixQ = new THREE.Quaternion();
const _hp = new THREE.Vector3();
const _solveShoulder = new THREE.Vector3();
const _bx = new THREE.Vector3();
const _by = new THREE.Vector3();
const _bz = new THREE.Vector3();
const _bm = new THREE.Matrix4();
// contact-fit scratch (build time only, but the no-allocation rule holds anyway)
const _fitInv = new THREE.Matrix4();
const _fitP = new THREE.Vector3();
const _fitD = new THREE.Vector3();
const _fitAxis = new THREE.Vector3();
const _fitAx0 = new THREE.Vector3();
const _fitM = new THREE.Matrix4();

/**
 * Orient a bone whose geometry runs along its local -Z so that -Z points along
 * `dir`, with local +Y rolled toward `up`.
 *
 * This deliberately does NOT use Object3D.lookAt(): for non-camera objects
 * lookAt aims local **+Z** at the target (so a -Z bone would point backwards),
 * and it interprets the target in WORLD space, which is wrong here because
 * every joint position is authored in the rig's local space.
 */
function aimBone(quat, dir, up) {
  _bz.copy(dir).multiplyScalar(-1).normalize(); // local +Z is opposite the bone
  _by.copy(up);
  _by.addScaledVector(_bz, -_by.dot(_bz));
  if (_by.lengthSq() < 1e-9) {
    // Degenerate roll reference: pick any axis that is not parallel to the bone.
    _by.set(0, 1, 0).addScaledVector(_bz, -_bz.y);
    if (_by.lengthSq() < 1e-9) _by.set(1, 0, 0).addScaledVector(_bz, -_bz.x);
  }
  _by.normalize();
  _bx.crossVectors(_by, _bz).normalize();
  _bm.makeBasis(_bx, _by, _bz);
  return quat.setFromRotationMatrix(_bm);
}

/**
 * One arm: shoulder -> upper -> fore -> hand, solved from the hand target.
 * All positions are expressed in the arm root's parent space (the viewmodel
 * rig's space), which is what makes the maths trivial.
 */
export class Arm {
  constructor(side, materials, opts = {}) {
    this.side = side; // -1 left, +1 right
    this.scale = opts.scale ?? 1;
    this.l1 = (opts.upper ?? L_UPPER) * this.scale;
    this.l2 = (opts.fore ?? L_FORE) * this.scale;

    this.root = new THREE.Object3D();
    this.root.name = side < 0 ? 'arm-left' : 'arm-right';
    /** Kept so `bakeSurfaceMasks` can classify a mesh by which surface it wears. */
    this._mats = materials;

    this.shoulder = new THREE.Vector3(
      side * (opts.shoulderX ?? 0.19),
      opts.shoulderY ?? -0.19,
      opts.shoulderZ ?? 0.12
    );
    // Some reload keyframes deliberately put the support hand below the
    // magazine, beyond a relaxed shoulder's reach.  Let the clavicle follow a
    // small, bounded amount in those exceptional frames so the wrist remains a
    // continuous joint instead of aiming a fixed-length forearm at an
    // unreachable hand.  This lives inside the cosmetic arm rig; camera,
    // weapon anchors, animation timing and player controller stay untouched.
    this.shoulderFollowMax = (opts.shoulderFollowMax ?? 0.22) * this.scale;
    this.reach = { target: 0, solved: 0, shoulderFollow: 0, error: 0, elbowPhi: 0, elbowFeasible: 1, wristBreak: 0, wristClamped: 0 };
    /**
     * Elbow swing direction, in the ARM ROOT's space (= the viewmodel rig's
     * space), NOT in hand space.
     *
     * Expressing the pole in hand space is the intuitive choice and it is wrong:
     * the support hand is rolled palm-up on the handguard, so its local "down"
     * points at the sky and the elbow swings UP — straight through the near
     * plane, filling half the screen with forearm. Elbows go down and outboard,
     * always, exactly as they do on a real shooter.
     */
    this.pole = new THREE.Vector3(side * 0.46, -0.86, 0.22).normalize();
    /**
     * Wrist break budget, radians, and how far the elbow scan may wander.
     *
     * A wrist is not a ball joint. Combined flexion/extension plus radial and
     * ulnar deviation tops out near 70 degrees from neutral on a live hand, and
     * a GRIPPING hand is nowhere near that, so anything over ~0.45 rad reads as
     * dislocated. 0.40 rad (23 deg) is the target the solve aims at.
     *
     * `wristAlignMax` is dc709e6's constraint, kept as it was written: that
     * commit swung the authored pole toward the point straight behind the hand
     * and capped the swing at 0.9 rad "so the pistol elbow stays out of
     * frame". The scan below searches that same arc for the best wrist rather
     * than aiming at one guess, which is strictly more than the swing could
     * do, but the ARC is the measured part and it stays.
     *
     * `wristHardMax` is 2.1 rad (120 deg) and is a BACKSTOP, not a mechanism.
     * It was 1.05, and that was too eager to be safe: a 0.74 rad clamp fired
     * on the trigger hand in ordinary idle frames and rotated the glove clean
     * off the pistol grip into the middle of the screen, which is a far worse
     * defect than the wrist angle it was correcting. A backstop that fires
     * during normal play is not a backstop. 1.45 rad is past anything a hand
     * does, so reaching it now means something upstream is genuinely broken:
     * it only fires on a target no elbow on the circle can hold, and when it
     * does it rotates the hand off the anchor, which moves the glove on the
     * weapon and invalidates the build-time fingertip fit. It must not fire in
     * idle, ADS or fire frames on any weapon.
     */
    this.wristLimit = opts.wristLimit ?? 0.40;
    /**
     * How far the elbow may be steered off `pole` for the wrist's sake.
     *
     * From dc709e6, which measured it: past about a radian off the pole the
     * pistol elbow rises into frame. The scan below searches the circle for the
     * best wrist angle, and this is the arc it is allowed to search.
     */
    this.wristAlignMax = opts.wristAlignMax ?? 0.9;
    this.wristHardMax = opts.wristHardMax ?? 2.1;
    this.poleWeight = opts.poleWeight ?? 0.55;
    /**
     * Gait phase offset around the elbow circle and how hard to follow it.
     * The weight is written per frame and is zero at rest, so the pole term
     * alone decides where an idle elbow sits.
     */
    this.elbowPhiBias = 0;
    this.elbowGaitWeight = 0;

    // Bones. Geometry extends along -Z from each joint.
    /**
     * Sleeve radii.
     *
     * MEASURED, twice. At 78 mm across the elbow / 54 mm at the wrist the
     * support forearm rendered as a 160 px-wide smooth tube crossing the lower
     * third of every hipfire frame — "a huge untextured tan tube", and the single
     * most-cited defect in the whole build. The width is not the only problem
     * (see the material and the mask bake) but it is a third of it: the support
     * forearm's closest approach to the eye is ~0.38 m, so every millimetre of
     * radius is 2.6 px of screen at 1080p.
     *
     * The original realistic profile read too slight in first person, especially
     * next to the rifle. Use a deliberately athletic combat-shirt silhouette:
     * 84 mm across the elbow tapering to 62 mm at the wrist. The shooting arm
     * keeps a fuller upper sleeve (it is almost entirely out of frame) so the
     * two arms still read as the same garment.
     *
     * Fold counts go UP, not down: with the tube narrower the folds are what
     * carry the silhouette, and each one is a crease the mask bake fills with
     * grime and a crown it rubs dust onto.
     */
    // Card 9b47fa2f: 136 mm at the shoulder, 120 mm at the elbow, so the upper
    // sleeve still swallows the thicker forearm's elbow end (see buildForearm).
    this.upper = buildSleeve(materials.sleeve, this.l1, 0.068 * this.scale, 0.06 * this.scale, {
      folds: 5,
      elbowPad: true,
      kind: 'upper',
      side,
    });
    /**
     * THE FOREARM IS BARE SKIN; THE GREEN IS A SHIRT SLEEVE OVER THE TOP OF IT.
     *
     * The card asks for "textured arms with skin color and t-shirt where top of
     * sleeve is green and visible", and the reference photo is a lit bare
     * forearm running out of frame with the sleeve hem crossing it at about
     * mid-forearm. Every previous pass read "sleeve" as "clothe the whole limb"
     * and shipped a green tube from elbow to glove, which is neither what the
     * card says nor what the photo shows — and it is why the only skin on the
     * rig was a material nothing used.
     *
     * So the forearm bone now carries TWO meshes: bare skin for its whole
     * length, and a shirt sleeve over the elbow-end 46% of it. Both things the
     * card asks for are then true at once — skin forearms, and a green sleeve
     * whose hem is on screen in every hipfire frame. The team colour survives
     * (see `sleeve` in materials.js), because the hem is the part you see.
     *
     * The skin tube is slightly narrower than the old sleeve: 80 mm across the
     * elbow, 51 mm at the wrist. The wrist figure is not cosmetic. At 58 mm the
     * forearm was DEEPER than the palm it joins (the hand is 32 mm through at
     * the knuckles), so the glove cuff that has to cover it hung 12 mm below
     * the palm's own contact plane — and with the palm seated on a handguard,
     * that put the hem 1.6 mm inside the weapon on a rig that was otherwise
     * clean. A wrist is narrower than a palm; building it the other way round
     * costs contact everywhere downstream.
     */
    // Card 9b47fa2f made it muscular and anatomical; see buildForearm. It is
    // now ~120 mm across the belly and 67 x 42 mm at the wrist, and that wrist
    // is still shallower than the 46 mm this note warns about.
    this.fore = buildForearm(materials.skin ?? materials.sleeve, this.l2, this.scale, side);
    /**
     * 32% of the forearm, not half of it. MEASURED on a rendered hipfire frame:
     * at 46% the sleeve filled the whole of the forearm that is actually ON
     * SCREEN (the elbow end is behind the near plane), leaving a 50 mm sliver of
     * skin at the wrist — so the limb still read as a green tube with a pink
     * cap, which is the failure this split exists to fix. At 32% the hem lands
     * where it does in the reference photo: roughly a third of the way up the
     * visible forearm, with bare skin from there to the glove.
     */
    this.foreSleeve = buildSleeve(
      materials.sleeve,
      this.l2 * 0.32,
      // Sized by tools/armfit.mjs to clear card 9b47fa2f's forearm belly at
      // every angle. The rounder 'upper' section, because the 'forearm' one
      // pinches the palm side straight into the flexor mass under it.
      0.068 * this.scale,
      0.063 * this.scale,
      { folds: 2, cuff: true, kind: 'upper', side }
    );
    this.upperPivot = new THREE.Object3D();
    this.forePivot = new THREE.Object3D();
    this.upperPivot.add(this.upper);
    this.forePivot.add(this.fore);
    this.forePivot.add(this.foreSleeve);
    // The glove's cuff rides the forearm, not the hand — see buildWristCuff.
    this.wristCuff = buildWristCuff(materials, this.scale);
    this.wristCuff.position.z = -this.l2;
    this.forePivot.add(this.wristCuff);
    this.root.add(this.upperPivot);
    this.root.add(this.forePivot);

    // Hand.
    this.hand = new THREE.Object3D();
    this.hand.name = side < 0 ? 'hand-left' : 'hand-right';
    this.handInner = new THREE.Object3D();
    /**
     * CHIRALITY. The basis built by handBasis is right-handed with X = Y cross Z,
     * so for a hand whose fingers run along -Z and whose palm faces -Y, +X points
     * AWAY from the thumb on a right hand and TOWARD it on a left hand. The
     * geometry below puts the thumb at +X, which makes the authored mesh a LEFT
     * hand — so it is the RIGHT arm that needs the mirror, not the left.
     *
     * With this the wrong way round the shooting hand was a left hand on the
     * right side of the grip: the index (which setTrigger drives) came out at the
     * bottom-rear of the grip instead of on the trigger, and no choice of target
     * frame could fix it, because putting the thumb at the top of the grip forced
     * the fingers to wrap backwards around the back strap.
     */
    this.handInner.scale.x = side < 0 ? 1 : -1;
    /**
     * HAND SCALE, separate from the arm's.
     *
     * The rebuilt glove is a physically bigger object than the one it replaced
     * — a knuckle guard standing proud, a cuff, spherical knuckles, quilting —
     * and on a weapon held at arm's length that is the difference between a
     * hand you can read and a hand that has eaten the gun. MEASURED on the
     * shotgun at idle: the trigger glove took the weapon's unoccluded pixels
     * from 26 686 on main to 7 920. The reference photo's glove spans about a
     * quarter of the frame's width; at 1.0 this one spans nearly half.
     *
     * So the glove is scaled on its own, leaving the BONES alone: the reach,
     * the elbow solve and the sleeve are untouched, and only the hand gets
     * smaller. It is applied to `handInner`, which already carries the
     * chirality mirror, so the sign has to survive.
     */
    // `Viewmodel._applyHandScale` overrides this per weapon; what is kept here
    // is the default it falls back to.
    this.handScale = opts.handScale ?? 1;
    const hs = this.handScale;
    this.handInner.scale.set(this.handInner.scale.x * hs, hs, hs);
    this.hand.add(this.handInner);
    /**
     * The two points the palm is seated on, in the spaces that own them: the
     * heel pad belongs to the hand (bone 0) and the metacarpal-head patch to
     * the arch (bone 1). `fitToCylinder` drives the arch until both are on the
     * handguard; `Viewmodel._placeSupportHand` puts the heel one there first.
     * Kept in step with the `heelPad` and `patch` blobs in `buildGlove`.
     */
    const H = 0.032 * this.scale;
    const PL = 0.098 * this.scale;
    this.palmContact = {
      heel: [0, -H * 0.56, -PL * 0.14],
      head: [0, -H * 0.545 + 0.004 * this.scale, -PL * 0.6 + 0.031 * this.scale],
    };
    const built = buildGlove(materials, { scale: this.scale });
    this.glove = built.root;
    /**
     * The carpometacarpal hinge. Every finger, the thumb, the knuckle guard and
     * the metacarpal half of the palm hang off this, so flexing it folds the
     * palm around whatever the hand is holding. See `buildGlove`.
     */
    this.palmArch = built.arch;
    this.handInner.add(this.glove);
    this.root.add(this.hand);

    // Fingers: index is separate so it can work the trigger.
    const fingerSpecs = [
      { x: 0.0298, len: [0.045, 0.028, 0.022], r: [0.0102, 0.0096, 0.0086, 0.0062] }, // index
      { x: 0.0102, len: [0.049, 0.031, 0.023], r: [0.0104, 0.0098, 0.0088, 0.0064] },
      { x: -0.0104, len: [0.046, 0.029, 0.022], r: [0.01, 0.0094, 0.0084, 0.006] },
      { x: -0.0298, len: [0.038, 0.024, 0.02], r: [0.0092, 0.0086, 0.0078, 0.0056] },
    ];
    this.fingers = [];
    // Per-segment dimensions, kept so `fitToCylinder` can walk the chain without
    // re-deriving them.
    this._segRadius = fingerSpecs.map((s) => s.r.map((v) => v * this.scale));
    this._segLength = fingerSpecs.map((s) => s.len.map((v) => v * this.scale));
    for (let i = 0; i < 4; i++) {
      const sp = fingerSpecs[i];
      const f = buildFinger(materials, {
        lengths: sp.len.map((v) => v * this.scale),
        radii: sp.r.map((v) => v * this.scale),
        curl: [0, 0, 0],
      });
      // The metacarpophalangeal joints sit on the PALMAR half of the hand, not on
      // its centre line. 3.5 mm dorsal put every finger's axis 10 mm further from
      // whatever the hand was gripping than the palm's own contact surface, so a
      // palm placed flush on a handguard still left the fingers hovering 8-14 mm
      // clear of it — the daylight the critique measured. -6 mm puts the finger
      // axis 8 mm off the palm's contact plane, which is one finger radius.
      // Authored in HAND space and re-based into the arch's, which is a plain
      // subtraction because the arch sits at identity rotation in the bind pose.
      f.root.position
        .set(sp.x * this.scale, -0.006 * this.scale, -0.096 * this.scale)
        .sub(this.palmArch.position);
      // fingers fan out very slightly
      f.root.rotation.y = -sp.x * 2.2;
      this.palmArch.add(f.root);
      this.fingers.push(f);
    }
    this.thumb = buildThumb(materials, this.scale, THUMB);
    // The carpometacarpal joint is palmar and a little further into the hand than
    // the old placement: a thumb rooted on the hand's centre plane rotates in the
    // plane of the back of the hand, which is why the old one read as a spur.
    this.thumb.root.position
      // 9 mm palmar was a thumb hung off the bottom of the hand. The
      // carpometacarpal joint sits close to the hand's own centre plane, and
      // the difference is not cosmetic: the ball on that joint is invariant
      // under every rotation the thumb has, so whatever depth it is authored
      // at is a floor on how close the palm can get to a handguard. At -9 mm
      // it was the last 1.1 mm of interpenetration left on the whole glove.
      .set(0.037 * this.scale, -0.0035 * this.scale, -0.04 * this.scale)
      .sub(this.palmArch.position);
    this.thumb.root.rotation.set(0.2, -0.95, -0.5);
    this.palmArch.add(this.thumb.root);

    // Same rule as the weapon: receive the world sun shadow, cast nothing.
    this.root.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = false;
        o.receiveShadow = true;
        o.frustumCulled = false;
      }
    });

    /**
     * Per-weapon pose overrides, written by `fitToCylinder`. `setPose` looks here
     * first, so a pose solved against one weapon's handguard cannot leak onto
     * another's — and, critically, a clip that swaps the support hand to 'open'
     * and back to 'clamp' restores the FITTED clamp, not the authored one.
     */
    this.poses = {};
    /** See `setPose`; written by Viewmodel._alignTriggerWrist. */
    this.archOffset = 0;

    this.setPose(opts.pose ?? 'wrap');
  }

  /**
   * BUILD-TIME CONTACT SOLVE: clamp every fingertip onto a cylinder.
   *
   * The authored `clamp` curls were derived analytically from a 47 mm tube and
   * one nominal contact clock angle, and on paper they put the PIP, DIP and tip
   * all 8.2 mm off the surface. On screen they did not: in hero, detail, weapon
   * and ads the distal segments visibly stood clear of the handguard, because the
   * analytic solve ignored (a) the 0.88 Y-scale on the finger capsules, (b) the
   * -6 mm palmar offset of the MCP row, (c) the fan-out rotation on each finger
   * root and (d) the fact that the four fingers start at four different X, so
   * they meet the cylinder at four different clock angles.
   *
   * Rather than push more algebra at it, MEASURE it: pose the hand, walk the real
   * transform chain to each fingertip's contact patch, and search the distal
   * joint's own rotation for the value that lands the patch on the surface. That
   * is a raycast against the collision profile in all but name, and it is exact
   * by construction because it uses the same matrices the renderer will.
   *
   * The thumb is fitted the same way but wraps to the OPPOSITE side of the tube:
   * a C-clamp whose thumb is on the same side as the fingers is a fist held next
   * to the gun, not a grip on it.
   *
   * @param {THREE.Vector3}  handPos    wrist target, arm-root space
   * @param {THREE.Quaternion} handQuat wrist orientation
   * @param {number[]} axisPoint  a point on the cylinder axis, arm-root space
   * @param {number[]} axisDir    the cylinder axis direction
   * @param {number}   radius     cylinder radius
   * @param {object}   opts       { clearance, poseName }
   * @returns {THREE.Vector3[]}   contact points, arm-root space (for baked AO)
   */
  fitToCylinder(handPos, handQuat, axisPoint, axisDir, radius, opts = {}) {
    const clearance = opts.clearance ?? 0.001;
    const poseName = opts.poseName ?? this.pose;
    const base = this.poses[poseName] ?? HAND_POSES[poseName] ?? HAND_POSES.clamp;

    this.hand.position.copy(handPos);
    this.hand.quaternion.copy(handQuat);
    this.root.updateMatrixWorld(true);
    // Everything is measured in the ARM ROOT's space, so the result is
    // independent of wherever the rig happens to be this frame.
    _fitInv.copy(this.root.matrixWorld).invert();
    _fitAxis.set(axisDir[0], axisDir[1], axisDir[2]).normalize();
    const ax0 = _fitAx0.set(axisPoint[0], axisPoint[1], axisPoint[2]);

    /** Signed distance from a joint-local point to the cylinder surface. */
    const gapAt = (joint, lx, ly, lz, out) => {
      joint.updateWorldMatrix(true, true);
      _fitP.set(lx, ly, lz).applyMatrix4(joint.matrixWorld).applyMatrix4(_fitInv);
      if (out) out.copy(_fitP);
      _fitD.copy(_fitP).sub(ax0);
      _fitD.addScaledVector(_fitAxis, -_fitD.dot(_fitAxis));
      return _fitD.length() - radius;
    };

    /**
     * Scan a joint's flexion for the angle that puts `local` on the surface.
     *
     * A scan, not a bisection: the gap is not monotonic in curl (past ~110 deg
     * the tip starts coming back OUT the far side of the tube), so a bisection
     * can converge on the wrong root. 40 samples over the anatomical range is
     * 2.5 deg of resolution, which is 0.4 mm at the fingertip.
     */
    const fitJoint = (joint, local, lo, hi, standoff = 0, smoothTo = null) => {
      let best = joint.rotation.x;
      let bestCost = Infinity;
      for (let i = 0; i <= 48; i++) {
        const a = lo + ((hi - lo) * i) / 48;
        joint.rotation.x = a;
        const g = gapAt(joint, local[0], local[1], local[2]) - standoff;
        // Target: on the surface, up to `clearance` proud, at most 1.5 mm buried.
        let cost = Math.abs(g - clearance * 0.5) + (g < -0.0015 ? (-g - 0.0015) * 8 : 0);
        /**
         * SMOOTHNESS TERM. Contact alone does not pick a plausible finger: there
         * is a one-parameter family of chains that touch the tube, and the free
         * solve chose wildly uneven ones — measured on the shipped anchors, the
         * little finger came out 39 / 94 / 54 degrees, i.e. a 94-degree PIP next
         * to a 39-degree MCP. That is a hinge, and it is precisely the shape the
         * card complains about. Penalising the difference from the joint before
         * it costs a few tenths of a millimetre of contact and buys a curl whose
         * joints are within ~15 degrees of each other.
         *
         * Weight is in metres per radian: 0.004 means a 0.25 rad step costs 1 mm,
         * which is the same order as the contact term it trades against.
         */
        if (smoothTo !== null) cost += Math.abs(a - smoothTo) * 0.006;
        if (cost < bestCost) {
          bestCost = cost;
          best = a;
        }
      }
      joint.rotation.x = best;
      return best;
    };

    /**
     * Scan a joint's flexion against SEVERAL points at once.
     *
     * A single-point contact cost is indifferent to where the rest of the part
     * went, and on a cylinder that is not a detail: for any one point there are
     * two rotations that put it on the surface — one that lays the part along
     * the tube and one that drives it straight through — and they cost the same.
     * MEASURED: the arch fitted on its metacarpal-head patch alone reported 0.8
     * mm of contact while the metacarpal row it carries was 12.3 mm inside the
     * handguard, taking 5.3% of the glove's vertices with it.
     *
     * Burial is priced THIRTY times standoff, and the factor is load-bearing.
     * MEASURED at six: with five sample points on a distal phalanx, a curl that
     * put three of them on the surface and drove two 5 mm through cost less
     * than a straighter one that stood 15 mm off every point, so the solve
     * bought contact with interpenetration and the index fingertip came out
     * 11.6 mm inside the handguard. At thirty, no amount of standoff anywhere
     * is worth a millimetre of the weapon, which is the rule the renderer
     * enforces anyway: a fingertip inside the gun is the one error on this rig
     * that cannot be read as a stylistic choice.
     *
     * @param {Array} pts  [[joint, lx, ly, lz], ...] surface points to keep out
     */
    const fitJointMulti = (joint, pts, lo, hi, seat, smoothTo = null) => {
      let best = joint.rotation.x;
      let bestCost = Infinity;
      for (let i = 0; i <= 56; i++) {
        const a = lo + ((hi - lo) * i) / 56;
        joint.rotation.x = a;
        let cost = 0;
        for (const [j, lx, ly, lz, wt] of pts) {
          const g = gapAt(j, lx, ly, lz);
          // The optional weight marks the point that is supposed to BE the
          // contact, as opposed to the ones that only have to stay out of the
          // weapon. Without it the sum is indifferent between a finger whose
          // pad is on the handguard and one that hovers evenly 4 mm off it,
          // and the second is not a grip.
          cost += (g < seat ? (seat - g) * 30 : g - seat) * (wt ?? 1);
        }
        // Same smoothness term as the single-point fit, scaled by the number of
        // points so the two trade off against contact at the same rate.
        if (smoothTo !== null) cost += Math.abs(a - smoothTo) * 0.006 * pts.length;
        if (cost < bestCost) {
          bestCost = cost;
          best = a;
        }
      }
      joint.rotation.x = best;
      return best;
    };

    /**
     * Wrap all three joints, PROXIMAL FIRST.
     *
     * Fitting only the distal joint cannot wrap a cylinder: if the MCP and PIP
     * are authored for a different contact clock angle the finger traces the
     * wrong spiral, and the distal joint is then asked to close a gap it is 22 mm
     * long and physically cannot reach. Solving the chain outward — each joint
     * placing the NEXT joint's origin one finger-radius off the surface, then the
     * distal joint placing the actual contact patch on it — is what a finger does,
     * and it is stable because each stage only has one degree of freedom.
     */
    /**
     * THE ARCH IS SOLVED FIRST, and it has to be first: it carries every finger
     * root, so fitting the fingers against an unfolded palm fits them to the
     * wrong place and the whole chain has to be redone.
     *
     * The heel is rigid with respect to the wrist, so the caller
     * (`Viewmodel._placeSupportHand`) is what puts it on the tube; all the arch
     * can do is bring the metacarpal HEADS down onto the tube as well, which is
     * precisely the degree of freedom a flat palm was missing. Range stops at
     * 1.15 rad: past that the palm is folded into a fist and the knuckle guard
     * starts pointing at the barrel.
     */
    const archBase = base.arch ?? 0;
    this.palmArch.rotation.x = -archBase;
    /**
     * Sampled down the whole metacarpal plank, not just at its contact patch:
     * the plank is 65 mm long on a 42-54 mm tube, so a rotation that seats its
     * middle can still put its far end a centimetre inside the weapon. The row
     * of metacarpal heads is the far end, and it is the one that carries every
     * finger.
     */
    const APL = 0.098 * this.scale;
    const AH = 0.032 * this.scale;
    const ay0 = 0.004 * this.scale;
    const az0 = 0.031 * this.scale;
    const archPts = [
      [this.palmArch, 0, -AH * 0.5 + ay0, -APL * 0.4 + az0],
      // The metacarpal-head patch is the contact the arch exists to make.
      [this.palmArch, this.palmContact.head[0], this.palmContact.head[1], this.palmContact.head[2]],
      [this.palmArch, 0, -AH * 0.5 + ay0, -APL * 0.78 + az0],
      // The metacarpal-head ROW is the contact that matters, because it is
      // where the fingers start: fold the arch until that is on the tube and
      // the fingers have an ordinary wrap to do. Seating the middle of the palm
      // instead leaves the row 33 mm out on a 42 mm handguard, and four fingers
      // then saturate every joint trying to cross that gap and spiral through
      // the weapon.
      [this.palmArch, 0, -AH * 0.42 + ay0, -APL * 0.94 + az0, 5],
    ];
    /**
     * The thumb's carpometacarpal ball rides on the arch, and a ball centred on
     * its own joint origin is invariant under every rotation that joint has —
     * so if the arch folds it into the weapon, no amount of thumb solving can
     * get it out again. MEASURED: 2.7 mm of it inside the handguard, fixed, on
     * an otherwise clean glove. It belongs in the arch's cost, not the thumb's.
     */
    {
      const tp0 = this.thumb.root.position;
      const tbr = THUMB.r0 * 1.06 * this.scale;
      // Weighted low on purpose: 24 points at full weight simply outvoted the
      // four on the plank and the arch unfolded to protect the thumb at the
      // cost of every finger. These only have to keep the ball OUT of the
      // weapon, which the 30x burial term already does on its own.
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        for (const zf of [-0.4, 0, 0.4]) {
          const r = tbr * Math.sqrt(Math.max(0, 1 - zf * zf));
          archPts.push([
            this.palmArch,
            tp0.x + Math.sin(a) * r,
            tp0.y - Math.cos(a) * r,
            tp0.z + zf * tbr,
            0.12,
          ]);
        }
      }
    }
    const archAngle = -fitJointMulti(this.palmArch, archPts, -1.15, 0.02, clearance);

    const fingers = [];
    const contacts = [];
    for (let i = 0; i < 4; i++) {
      const f = this.fingers[i];
      const curl = base.fingers[i].slice();
      for (let j = 0; j < 3; j++) f.joints[j].rotation.x = -curl[j];
      const rr = this._segRadius?.[i] ?? [0.01, 0.0094, 0.0084, 0.006];
      const ll = this._segLength?.[i] ?? [0.046, 0.029, 0.022];
      /**
       * RANGE CAP. The old limits (-1.75, i.e. 100 degrees, on the MCP and PIP
       * and -1.95 on the DIP) are well past what any joint of a finger does and
       * they let the free solve park a whole wrap on one hinge — measured, the
       * little finger came out 39 / 94 / 54 degrees.
       *
       * 1.45 rad is 83 degrees, which is a hard grip and is what a finger
       * closing on a 54 mm tube actually does. It was 1.3 for one round and
       * that was too tight to be kind: with the metacarpal row 30 mm off a tube
       * that small, capping the MCP and PIP at 74 degrees pinned BOTH of them
       * at the limit and left the whole remaining wrap to the distal joint,
       * which then overshot and drove the fingertip 11 mm inside the weapon.
       * A cap that forces the chain onto one hinge re-creates the very defect
       * it was added to prevent.
       */
      const LO = -1.45;
      /**
       * EVERY JOINT IS FITTED AGAINST ITS WHOLE SEGMENT'S SURFACE, not against
       * one point on its axis.
       *
       * The axis-and-standoff version read as contact and was not: MEASURED on
       * the seated palm, the index finger's distal joint reported its grip patch
       * 0.6 mm off the handguard while the fingertip 11 mm further on was 12.3
       * mm INSIDE it, because a single point admits both the rotation that lays
       * the segment along the tube and the one that drives it through, at equal
       * cost. Three points down the palmar surface of each phalanx — and, on the
       * distal one, the tip ball as well — leaves only the first.
       */
      let prev = null;
      const allPts = [];
      for (let j = 0; j < 3; j++) {
        const jr0 = rr[j];
        const jr1 = rr[j + 1];
        const jl = ll[j];
        /**
         * ROUND the phalanx, not just underneath it.
         *
         * Palmar-only sampling is blind to the failure it most needs to catch.
         * A finger that over-closes does not meet the handguard with its pad —
         * it comes round the far side and goes in BACK FIRST, and every point
         * the solve was looking at is on the other side of the digit. MEASURED
         * on the shotgun: 8.9 mm of the index finger inside the magazine tube
         * with every sampled point reporting clearance, and a relaxation pass
         * that therefore had nothing to relax.
         *
         * Six clock angles at three stations, at 1.1x the shaft radius so the
         * grip pad and the quilting (which stand ~7% proud) are the surfaces
         * being kept out rather than the shell under them. The palmar point at
         * mid-phalanx keeps its weight: that one is meant to TOUCH.
         */
        const pts = [];
        for (const [f0, rA] of [[0.25, jr0], [0.6, (jr0 + jr1) * 0.5], [0.95, jr1]]) {
          for (let k = 0; k < 6; k++) {
            const a = (k / 6) * Math.PI * 2;
            pts.push([
              f.joints[j],
              Math.sin(a) * rA * 1.1,
              -Math.cos(a) * rA * 1.1,
              -jl * f0,
              k === 0 && f0 === 0.6 ? 5 : 1,
            ]);
          }
        }
        if (j === 2) {
          // The fingertip ball, round and along: its centre sits one radius back
          // from the end of the phalanx.
          for (let k = 0; k < 6; k++) {
            const a = (k / 6) * Math.PI * 2;
            for (const zf of [-0.5, 0.3, 0.85]) {
              const rb = rr[3] * Math.sqrt(Math.max(0, 1 - zf * zf));
              pts.push([
                f.joints[2],
                Math.sin(a) * rb,
                -Math.cos(a) * rb,
                -(jl - rr[3]) - zf * rr[3],
              ]);
            }
          }
        }
        const a = fitJointMulti(f.joints[j], pts, j === 2 ? LO - 0.1 : LO, -0.05, clearance, prev);
        curl[j] = -a;
        prev = a;
        for (const q of pts) allPts.push(q);
      }
      /**
       * RELAXATION: nothing may end up inside the weapon, ever.
       *
       * The per-joint fit is a greedy chain and a greedy chain can paint itself
       * into a corner. On the smaller handguards — the shotgun's magazine tube
       * is 42 mm across against the carbine's 54 — the metacarpal row sits 33
       * mm off a tube whose whole circumference is 131 mm, so a finger trying
       * to lie along it saturates every joint, spirals past the far side and
       * comes back through the gun: MEASURED at 10.1 mm inside on the shotgun,
       * 10.9 on the ak, 8.9 on the sniper, with every joint pinned at its
       * limit. Loosening the limit does not fix it, it just moves which finger
       * fails.
       *
       * So the fit is followed by a hard guarantee rather than a better guess:
       * while anything on this finger is inside the surface, back the
       * most-flexed joint off a little and look again. It costs contact on the
       * fingers that were over-closing and nothing at all on the ones that were
       * not, and it cannot leave a fingertip inside the weapon.
       */
      for (let pass = 0; pass < 40; pass++) {
        let worst = Infinity;
        for (const [jt, lx, ly, lz] of allPts) {
          const g = gapAt(jt, lx, ly, lz);
          if (g < worst) worst = g;
        }
        if (worst > -0.0005) break;
        let k = 0;
        for (let m = 1; m < 3; m++) {
          if (f.joints[m].rotation.x < f.joints[k].rotation.x) k = m;
        }
        f.joints[k].rotation.x = Math.min(0, f.joints[k].rotation.x + 0.035);
      }
      for (let j = 0; j < 3; j++) curl[j] = -f.joints[j].rotation.x;
      // The fingertip grip patch, for the contact-AO bake: palmar side, one
      // radius below the axis, half way along the distal segment — the same
      // numbers as the `gripPad` blob in buildFinger, so mask and mesh agree.
      const local = [0, -rr[3] * 1.05, -ll[2] * 0.5];
      fingers.push(curl);
      const p = new THREE.Vector3();
      gapAt(f.joints[2], local[0], local[1], local[2], p);
      contacts.push(p);
    }

    /**
     * ---- thumb: laid ALONG the tube, and never through it ------------------
     *
     * The old solve aimed the thumb over the top and down the FAR side, and
     * that was right for the hand it was written for: a C-clamp whose palm
     * stood 50 mm off a 54 mm tube, where the thumb was the only thing that
     * could bridge the gap. With the palm seated on the handguard
     * (`Viewmodel._placeSupportHand`) that same instruction drives the thumb
     * straight through the weapon — MEASURED at 12.3 mm inside the cylinder,
     * the deepest interpenetration left on the whole glove, and 5.3% of the
     * glove's vertices with it.
     *
     * Two changes, and the second is the one that matters:
     *
     *  1. The cost is sampled at FOUR points down the thumb — mid-metacarpal,
     *     the interphalangeal joint, mid-distal and the tip pad — not at the
     *     tip alone. A single-point contact cost is indifferent to where the
     *     rest of the digit went, which is exactly how a solve that reported
     *     0.4 mm of fingertip contact could have 60 mm of thumb inside the gun.
     *  2. Burial is priced six times standoff, so the minimum is the thumb
     *     LYING ON the surface rather than crossing it. The photo's thumb does
     *     the same thing: it runs along the pump, not around it.
     *
     * The base's two saddle axes are scanned first with the hinges mid-flexed,
     * then both hinges are scanned together against the same four-point cost —
     * jointly, because they trade off against each other and fitting them in
     * series re-creates the single-point blindness one joint at a time.
     */
    const thumbBase = (base.thumbBase ?? [0, 0, 0]).slice();
    const thumb = (base.thumb ?? [0.3, 0.24]).slice();
    this.thumb.root.rotation.fromArray(thumbBase);
    const tr0 = (THUMB.r0 + THUMB.r1) * 0.5 * this.scale;
    const tr1 = (THUMB.r1 + THUMB.r2) * 0.5 * this.scale;
    const tl0 = THUMB.l0 * this.scale;
    const tlen = THUMB.l1 * this.scale;
    const tr = THUMB.r2 * this.scale;
    const tLocal = [0, -tr * 1.05, -tlen * 0.55];
    /**
     * The thumb's surface, sampled round AND along it.
     *
     * Palmar-only sampling was not enough: once the palm is seated, a thumb
     * swung across it meets the handguard side-on, and the point that goes
     * through the weapon is on a flank rather than underneath. MEASURED at
     * -9.8 mm with four palmar points; eighteen points over three stations on
     * the metacarpal and three on the distal phalanx, at six clock angles
     * each, leaves nothing to find.
     */
    const tPts = [];
    /**
     * 1.32x the shaft radius, because the grip pads are what actually touch.
     * MEASURED: sampling the bare shaft left 2.7 mm of the thumb's palmar grip
     * pad inside the handguard — its corners sit at 1.30 r, and a solve that
     * cannot see a surface will happily put it through the weapon. The same
     * factor covers the finger pads, for the same reason.
     */
    const PAD = 1.55;
    for (const [joint, len, rad, from] of [
      [this.thumb.joints[0], tl0, tr0 * PAD, 0],
      [this.thumb.joints[1], tlen, tr1 * PAD, 0],
    ]) {
      for (const f of [from - 0.12, from, 0.35, 0.7, 0.95]) {
        for (let k = 0; k < 10; k++) {
          const a = (k / 10) * Math.PI * 2;
          tPts.push([joint, Math.sin(a) * rad, -Math.cos(a) * rad, -len * f]);
        }
      }
    }
    tPts.push([this.thumb.joints[1], tLocal[0], tLocal[1], tLocal[2]]);
    const seat = clearance;
    const thumbCost = () => {
      let c = 0;
      for (const [j, lx, ly, lz] of tPts) {
        const g = gapAt(j, lx, ly, lz);
        c += g < seat ? (seat - g) * 30 : g - seat;
      }
      return c;
    };
    {
      // Mid-flex the hinges while the base is searched, so the scan measures
      // where a naturally curled thumb would land rather than a straight one.
      this.thumb.joints[0].rotation.x = -0.5;
      this.thumb.joints[1].rotation.x = -0.4;
      /**
       * ALL THREE base axes, not two. The carpometacarpal joint is a saddle and
       * the two-axis scan was written for a hand whose palm was 50 mm off the
       * tube, where the thumb only ever had to reach forward. With the palm
       * seated, the thumb's rest direction points into the weapon and the axis
       * that lifts it off — rotation about the metacarpal's own X — was the one
       * being held fixed, so every candidate the scan could see was buried.
       * MEASURED: 9.8 mm of thumb inside the handguard, 2.8% of the glove.
       */
      const x0 = thumbBase[0];
      const y0 = thumbBase[1];
      const z0 = thumbBase[2];
      let bestX = x0;
      let bestY = y0;
      let bestZ = z0;
      let bestCost = Infinity;
      for (let q = 0; q <= 12; q++) {
        const xx = x0 - 1.2 + (2.4 * q) / 12;
        this.thumb.root.rotation.x = xx;
        for (let i = 0; i <= 18; i++) {
          const yy = y0 - 1.6 + (3.2 * i) / 18;
          this.thumb.root.rotation.y = yy;
          for (let k = 0; k <= 14; k++) {
            const zz = z0 - 1.2 + (2.4 * k) / 14;
            this.thumb.root.rotation.z = zz;
            // A small pull toward the authored pose so the solve stays plausible
            // where several placements are equally good.
            const cost =
              thumbCost() + (Math.abs(xx - x0) + Math.abs(yy - y0) + Math.abs(zz - z0)) * 0.0012;
            if (cost < bestCost) {
              bestCost = cost;
              bestX = xx;
              bestY = yy;
              bestZ = zz;
            }
          }
        }
      }
      this.thumb.root.rotation.set(bestX, bestY, bestZ);
      thumbBase[0] = bestX;
      thumbBase[1] = bestY;
      thumbBase[2] = bestZ;
    }
    {
      // Both hinges together. 26 x 26 over the anatomical range is 2.9 degrees
      // of resolution, which is 0.6 mm at the thumb tip.
      let bestA = -0.5;
      let bestB = -0.4;
      let bestCost = Infinity;
      for (let i = 0; i <= 26; i++) {
        const a = -1.35 + (1.33 * i) / 26;
        this.thumb.joints[0].rotation.x = a;
        for (let k = 0; k <= 26; k++) {
          const b = -1.5 + (1.45 * k) / 26;
          this.thumb.joints[1].rotation.x = b;
          const cost = thumbCost();
          if (cost < bestCost) {
            bestCost = cost;
            bestA = a;
            bestB = b;
          }
        }
      }
      this.thumb.joints[0].rotation.x = bestA;
      this.thumb.joints[1].rotation.x = bestB;
      thumb[0] = -bestA;
      thumb[1] = -bestB;
    }
    // Same guarantee as the fingers: back the thumb off until none of it is
    // inside the weapon, however good its contact looked.
    for (let pass = 0; pass < 40; pass++) {
      let worst = Infinity;
      for (const [jt, lx, ly, lz] of tPts) {
        const g = gapAt(jt, lx, ly, lz);
        if (g < worst) worst = g;
      }
      if (worst > -0.0005) break;
      const k = this.thumb.joints[1].rotation.x < this.thumb.joints[0].rotation.x ? 1 : 0;
      this.thumb.joints[k].rotation.x = Math.min(0, this.thumb.joints[k].rotation.x + 0.035);
    }
    thumb[0] = -this.thumb.joints[0].rotation.x;
    thumb[1] = -this.thumb.joints[1].rotation.x;
    const tp = new THREE.Vector3();
    gapAt(this.thumb.joints[1], tLocal[0], tLocal[1], tLocal[2], tp);
    contacts.push(tp);

    this.poses[poseName] = { fingers, thumb, thumbBase, arch: archAngle };
    this.pose = poseName;
    return contacts;
  }

  /**
   * BAKE CURVATURE MASKS ON THE WHOLE LIMB.
   *
   * This is the fix for "a huge UNTEXTURED tan tube" and "a rounded mitten of
   * stacked extruded ring segments".
   *
   * Every weapon mesh has had wear/grime/AO vertex masks baked since the first
   * build (see Viewmodel.addWeapon) — the arms never did. Their `color`
   * attribute was absent, so the shader read vColor = (0,0,0) and the wear,
   * grime and cavity-AO layers of `sleeve`, `glove`, `glove_pad` and
   * `glove_seam` were ALL switched off. Every one of those materials carries a
   * carefully tuned wear amplitude, a grime colour and an AO term that had
   * literally no effect on a single pixel: the arm was a flat albedo under a
   * smooth specular lobe, which is exactly what "untextured tube" means.
   *
   * Amplitudes are per surface class, because cloth, moulded TPR and a stitched
   * seam weather in completely different ways:
   *   cloth   broad, soft. The exponent stays LOW (1.6) so the mask spreads off
   *           the fold crease and dusts the whole crown — on fabric the dirt is
   *           not confined to the outer millimetre the way it is on a chamfer.
   *   pads    harder: a TPR knuckle cap polishes on its dome and collects grime
   *           in the flex gap around it, so wear is high and tight.
   *   seams   a proud sewn edge is the FIRST thing to go pale, so it takes the
   *           most wear of anything on the hand at the tightest exponent.
   *
   * @param {(geo: THREE.BufferGeometry, o: object) => void} bake   materials.bakeMasks
   * @param {(geo: THREE.BufferGeometry, o: object) => void} shape  mask re-shaper
   * @param {object} rng
   */
  bakeSurfaceMasks(bake, shape, rng = null) {
    if (!bake) return this;
    const m = this._mats ?? {};
    const CLOTH = { wearAmp: 0.5, wearExp: 1.6, grimeAmp: 1.0, grimeExp: 1.15, aoAmp: 0.9, aoExp: 1.1 };
    const SLEEVE = { wearAmp: 0.62, wearExp: 1.5, grimeAmp: 1.0, grimeExp: 1.0, aoAmp: 0.95, aoExp: 1.0 };
    const PAD = { wearAmp: 0.85, wearExp: 2.2, grimeAmp: 0.95, grimeExp: 1.4, aoAmp: 1.0, aoExp: 1.2 };
    const SEAM = { wearAmp: 1.0, wearExp: 2.6, grimeAmp: 0.7, grimeExp: 1.6, aoAmp: 0.8, aoExp: 1.2 };
    /**
     * Skin does not wear and it does not take grime the way cloth does — it
     * takes SHEEN on the crowns and shadow in the creases. So the wear layer is
     * nearly off and the AO is the strongest of the four surfaces: that is the
     * difference between a lit forearm and a tan tube.
     */
    const SKIN = { wearAmp: 0.18, wearExp: 2.0, grimeAmp: 0.35, grimeExp: 1.8, aoAmp: 1.0, aoExp: 0.9 };
    const done = new Set();
    this.root.traverse((o) => {
      if (!o.isMesh || done.has(o.geometry)) return;
      done.add(o.geometry);
      const prof =
        o.material === m.skin ? SKIN
          : o.material === m.sleeve ? SLEEVE
            : o.material === m.pad ? PAD
              : o.material === m.seam ? SEAM
                : CLOTH;
      // A lower edge threshold than the weapon's 0.16: the limb is all lathes and
      // blobs, so its creases are gentle and a hard-edge threshold finds nothing.
      bake(o.geometry, { wear: 1, grime: 1, ao: 1, edgeThreshold: 0.09, rng });
      shape(o.geometry, prof);
    });
    return this;
  }

  /**
   * Bake a contact-AO gradient into the GLOVE side of each contact.
   *
   * Geometric contact alone does not read as contact: two surfaces can be 0.5 mm
   * apart and still look like two floating objects, because nothing in the
   * lighting says they occlude each other. The cheap, correct cue is ambient
   * occlusion in the crevice — so the glove gets the same 0.55 multiply over a
   * 12 mm falloff that the handguard gets (see Viewmodel.addWeapon).
   *
   * The mask goes in vColor.b, which `materials/shader.js` uses as
   * `orm.r *= 1.0 - vColor.b * wear[2]`. The glove geometry carries no colour
   * attribute today, so the shader sees (0,0,0) — wear and grime OFF. Writing
   * (0, 0, ao) preserves that exactly and only lights up the AO term.
   *
   * @param {THREE.Vector3[]} contacts  contact points in arm-root space
   */
  bakeContactAO(contacts, radius = 0.012, peak = 0.9) {
    if (!contacts?.length) return this;
    this.root.updateMatrixWorld(true);
    _fitInv.copy(this.root.matrixWorld).invert();
    const r2 = radius * radius;
    this.glove.traverse((o) => {
      if (!o.isMesh) return;
      const geo = o.geometry;
      const pos = geo.getAttribute('position');
      if (!pos) return;
      let col = geo.getAttribute('color');
      if (!col || col.itemSize !== 3) {
        col = new THREE.BufferAttribute(new Float32Array(pos.count * 3), 3);
        geo.setAttribute('color', col);
      }
      _fitM.multiplyMatrices(_fitInv, o.matrixWorld);
      for (let i = 0; i < pos.count; i++) {
        _fitP.fromBufferAttribute(pos, i).applyMatrix4(_fitM);
        let closest = Infinity;
        for (const c of contacts) {
          const d2 = _fitP.distanceToSquared(c);
          if (d2 < closest) closest = d2;
        }
        if (closest > r2) continue;
        const t = 1 - Math.sqrt(closest) / radius;
        // smootherstep so the gradient has no visible terminator
        const s = t * t * t * (t * (t * 6 - 15) + 10);
        col.array[i * 3 + 2] = Math.max(col.array[i * 3 + 2], peak * s);
      }
      col.needsUpdate = true;
    });
    return this;
  }

  /** Static finger poses. The trigger finger is driven separately. */
  setPose(name) {
    const P = this.poses?.[name] ?? HAND_POSES[name] ?? HAND_POSES.wrap;
    for (let i = 0; i < 4; i++) {
      const curl = P.fingers[i];
      for (let j = 0; j < 3; j++) this.fingers[i].joints[j].rotation.x = -curl[j];
    }
    this.thumb.joints[0].rotation.x = -P.thumb[0];
    this.thumb.joints[1].rotation.x = -P.thumb[1];
    if (P.thumbBase) this.thumb.root.rotation.fromArray(P.thumbBase);
    /**
     * `archOffset` is how far the HAND has been pitched off its authored grip
     * frame by `Viewmodel._alignTriggerWrist`, and subtracting it here is what
     * makes that pitch free.
     *
     * Rotating the hand about its own knuckle axis straightens the wrist but
     * takes every finger with it, which would slide the grip. Rotating the
     * arch back by the same angle returns the metacarpal row, the fingers and
     * the thumb to exactly where the pose put them, so only the 31 mm of palm
     * BEHIND the arch hinge actually moves — which is precisely what lifts off
     * the back strap when a real wrist straightens.
     */
    this.palmArch.rotation.x = -(P.arch ?? 0) - this.archOffset;
    this.pose = name;
    return this;
  }

  /** Trigger-finger curl, 0 = off the trigger, 1 = fully pressed. */
  setTrigger(t) {
    const f = this.fingers[0];
    // Rest pose matches HAND_POSES.grip.fingers[0]: the finger is already ON the
    // trigger with the slack taken up, not standing off it straight.
    f.joints[0].rotation.x = -(0.5 + t * 0.3);
    f.joints[1].rotation.x = -(0.6 + t * 0.36);
    f.joints[2].rotation.x = -(0.46 + t * 0.34);
  }

  /**
   * Solve the two-bone chain so the hand lands exactly on `targetPos` with
   * orientation `targetQuat`, elbow swung toward the pole.
   */
  solve(targetPos, targetQuat) {
    this.hand.position.copy(targetPos);
    this.hand.quaternion.copy(targetQuat);

    _solveShoulder.copy(this.shoulder);
    _t.copy(targetPos).sub(_solveShoulder);
    let d = _t.length();
    const maxD = (this.l1 + this.l2) * 0.995;
    const minD = Math.abs(this.l1 - this.l2) * 1.05 + 1e-4;
    const targetD = d;
    let shoulderFollow = 0;
    // Before clamping the two-bone triangle, consume an unreachable residual by
    // moving the hidden shoulder socket toward (or, for a folded pose, away
    // from) the wrist.  The hand remains welded to its weapon/clip target and
    // the forearm still ends at that same target whenever the bounded follow is
    // sufficient.  This fixes the old split-wrist failure in reload/sprint
    // poses without changing the authored targets.
    if (d > maxD || (d < minD && d > 1e-5)) {
      _dir.copy(_t).divideScalar(d);
      const needed = d > maxD ? d - maxD : minD - d;
      shoulderFollow = Math.min(needed, this.shoulderFollowMax);
      _solveShoulder.addScaledVector(_dir, d > maxD ? shoulderFollow : -shoulderFollow);
      _t.copy(targetPos).sub(_solveShoulder);
      d = _t.length();
    }
    let residual = 0;
    if (d > maxD) {
      residual = d - maxD;
      _t.multiplyScalar(maxD / d);
      d = maxD;
    } else if (d < minD) {
      residual = minD - d;
      if (d < 1e-5) _t.set(0, 0, -minD);
      else _t.multiplyScalar(minD / d);
      d = minD;
    }
    _dir.copy(_t).divideScalar(d);
    this.reach.target = targetD;
    this.reach.solved = d;
    this.reach.shoulderFollow = shoulderFollow;
    this.reach.error = residual;

    // Circle of possible elbow positions; pick the point toward the pole.
    const a = (this.l1 * this.l1 - this.l2 * this.l2 + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(0, this.l1 * this.l1 - a * a));
    _pole.copy(this.pole);
    const k = this.wristAlign;
    if (k > 0) {
      // Where the elbow would sit if the forearm ran straight down the back of
      // the hand: wrist + l2 along the hand's +Z (the fingers point -Z). Swing
      // the authored pole toward it about the shoulder->wrist axis, but never
      // by more than `wristAlignMax`, so the elbow stays down and outboard.
      _perp.copy(_pole).addScaledVector(_dir, -_pole.dot(_dir));
      _hp.set(0, 0, this.l2).applyQuaternion(targetQuat).add(targetPos).sub(_solveShoulder);
      _hp.addScaledVector(_dir, -_hp.dot(_dir));
      if (_perp.lengthSq() > 1e-10 && _hp.lengthSq() > 1e-10) {
        _perp.normalize();
        _hp.normalize();
        const sign = Math.sign(_t.copy(_perp).cross(_hp).dot(_dir)) || 1;
        const ang = Math.min(Math.acos(THREE.MathUtils.clamp(_perp.dot(_hp), -1, 1)) * k, this.wristAlignMax);
        _pole.copy(_perp).applyAxisAngle(_dir, sign * ang);
      }
    }
    _perp.copy(_pole).addScaledVector(_dir, -_pole.dot(_dir));
    if (_perp.lengthSq() < 1e-8) {
      _perp.set(this.side, -1, 0).addScaledVector(_dir, 0);
      _perp.addScaledVector(_dir, -_perp.dot(_dir));
    }
    _perp.normalize();

    /**
     * THE ELBOW IS CHOSEN, NOT DECREED — and choosing it is what fixes the
     * snapped wrist.
     *
     * The elbow lies anywhere on a circle of radius `h` about the
     * shoulder-to-wrist line, and every point on that circle gives a different
     * FOREARM DIRECTION, hence a different angle between the forearm and the
     * hand's own metacarpal axis. That angle is the wrist break, and taking the
     * pole's point on the circle without looking at it is why the trigger hand
     * measured 121-125 degrees of break on every rifle in the catalog: a wrist
     * snapped back on itself, which is exactly the "90 degree bend" on the card.
     *
     * MEASURED, from the law of cosines on the solve's own triangle: with the
     * trigger hand 260 mm from its shoulder and a 630 mm arm, the forearm sweeps
     * a cone 108 degrees off the wrist-to-shoulder line while the hand axis sits
     * 125 degrees off it — so the achievable break ranges from 17 degrees to
     * 180, and the fixed pole was landing near the wrong end of that interval.
     * Nothing about the anchors or the bone lengths had to change; the solve was
     * simply not looking.
     *
     * So: scan the circle, score each candidate on how much it exceeds the
     * wrist limit, and break ties toward the authored pole so elbows still go
     * down and outboard and stay out of frame. 32 samples plus a local refine
     * is 1.3 degrees of resolution, which is far below what a wrist shows.
     *
     * `targetQuat` is required for this; when it is absent (a caller that only
     * wants a position) the authored pole is used unchanged.
     */
    _u1.copy(_perp);
    _u2.crossVectors(_dir, _u1).normalize();
    let bestPhi = 0;
    if (targetQuat) {
      _handZ.set(0, 0, -1).applyQuaternion(targetQuat).normalize();
      const limit = this.wristLimit;
      const scoreAt = (phi) => {
        const c = Math.cos(phi);
        const sn = Math.sin(phi);
        _cand.copy(_u1).multiplyScalar(c).addScaledVector(_u2, sn);
        _elbow.copy(_solveShoulder).addScaledVector(_dir, a).addScaledVector(_cand, h);
        _foreDir.copy(targetPos).sub(_elbow);
        if (_foreDir.lengthSq() < 1e-12) return Infinity;
        _foreDir.normalize();
        const brk = Math.acos(Math.min(1, Math.max(-1, _foreDir.dot(_handZ))));
        /**
         * THE ELBOW MUST NOT RISE, and that is tested directly rather than
         * approximated by refusing to leave the pole.
         *
         * dc709e6 bounded its pole swing at 0.9 rad because "past about a
         * radian off the pole the elbow rises into frame". That is a real
         * constraint and the right observation, but it is a proxy: bolted onto
         * this scan as a bound on its RANGE it also removed the candidates the
         * scan exists to find, and the trigger wrist went from 19-23 degrees
         * straight to the hard clamp at 60 on eight weapons. So the constraint
         * is expressed as itself — an elbow above its own shoulder is the thing
         * that ends up on screen — and the scan keeps the whole circle.
         */
        /**
         * The pole term is `1 - cos(phi - bias)`: 0 on the preferred point of
         * the circle and 2 opposite it. The BIAS is what makes the arm walk.
         *
         * Moving the shoulder socket is not enough on its own. The support arm
         * reaches its handguard at 91% of its own length, so its elbow is
         * nearly pinned to the shoulder-to-wrist line and a 66 mm socket swing
         * turns the forearm by 4 degrees — MEASURED, and visibly nothing. But
         * the elbow's CIRCLE is wide even at that extension (the forearm can
         * sweep 78 degrees around it on the rifle), so walking the preferred
         * point around the circle swings the forearm through a real arc with
         * the hand still welded to the weapon. That is the articulation the
         * card asks for, and it costs nothing: the wrist and elbow bounds are
         * still the terms that decide, the bias only breaks ties.
         */
        const cb = Math.cos(phi - this.elbowPhiBias);
        return (
          Math.max(0, brk - limit) * 3 +
          (1 - c) * this.poleWeight +
          (1 - cb) * this.elbowGaitWeight
        );
      };
      /**
       * Search the arc the pole is allowed to move over, not the whole circle.
       *
       * dc709e6 measured the constraint and it is the right one: past about a
       * radian off the authored pole the elbow rises into frame. Round three
       * replaced it with an explicit "stay in front of the eye" plane and an
       * elbow-height term, and that pair turned out to be a cliff — the elbow
       * jumped between two optima and the wrist went with it, 34 degrees on one
       * side and 74 on the other with nothing in between. The arc bound has no
       * cliff: it is one contiguous range of elbows, all of them out of shot.
       */
      const span = Math.min(Math.PI, this.wristAlignMax);
      const N = 48;
      const step = (2 * span) / N;
      let bestCost = Infinity;
      for (let i = 0; i <= N; i++) {
        const phi = -span + i * step;
        const cst = scoreAt(phi);
        if (cst < bestCost) {
          bestCost = cst;
          bestPhi = phi;
        }
      }
      for (let k = -4; k <= 4; k++) {
        const phi = bestPhi + (k * step) / 4;
        const cst = scoreAt(phi);
        if (cst < bestCost) {
          bestCost = cst;
          bestPhi = phi;
        }
      }
      this.reach.elbowFeasible = 1;
    }
    _perp.copy(_u1).multiplyScalar(Math.cos(bestPhi)).addScaledVector(_u2, Math.sin(bestPhi)).normalize();
    _elbow.copy(_solveShoulder).addScaledVector(_dir, a).addScaledVector(_perp, h);
    this.reach.elbowPhi = bestPhi;

    // Upper arm: shoulder -> elbow. The elbow pad sits on the bone's +Y, which
    // must end up on the OUTSIDE of the bend — that is the pole side.
    this.upperPivot.position.copy(_solveShoulder);
    _hp.copy(_elbow).sub(_solveShoulder);
    if (_hp.lengthSq() > 1e-12) aimBone(this.upperPivot.quaternion, _hp, _perp);

    // Forearm: elbow -> wrist, rolled with the back of the hand so the cuff and
    // the wrist line up with the glove.
    this.forePivot.position.copy(_elbow);
    _up.set(0, 1, 0).applyQuaternion(targetQuat);
    _hp.copy(targetPos).sub(_elbow);
    if (_hp.lengthSq() > 1e-12) aimBone(this.forePivot.quaternion, _hp, _up);

    /**
     * HARD CLAMP. The scan above gets the break inside `wristLimit` whenever the
     * elbow circle can reach it; when it cannot — an animation key that puts the
     * hand somewhere no arm can hold it that way — the wrist is bent the rest of
     * the way by rotating the HAND, not by leaving it snapped.
     *
     * This deliberately breaks the weld, by the smallest rotation that restores
     * a possible wrist: the hand position never moves, so the glove stays on the
     * weapon, and only its roll/pitch gives. A few degrees of slide at the grip
     * is invisible; a 125-degree wrist is the single most visible defect on the
     * viewmodel, and it is what the card is complaining about.
     *
     * It is a backstop, not the mechanism. If it is firing in ordinary idle or
     * ADS frames, the anchors are wrong and should be fixed instead — the arm
     * self-test asserts the break stays inside the limit without it.
     */
    this.reach.wristBreak = 0;
    this.reach.wristClamped = 0;
    if (_hp.lengthSq() > 1e-12) {
      _foreDir.copy(_hp).normalize();
      _handZ.set(0, 0, -1).applyQuaternion(this.hand.quaternion).normalize();
      const brk = Math.acos(Math.min(1, Math.max(-1, _foreDir.dot(_handZ))));
      this.reach.wristBreak = brk;
      if (brk > this.wristHardMax) {
        _axis.crossVectors(_handZ, _foreDir);
        if (_axis.lengthSq() > 1e-10) {
          _axis.normalize();
          const fix = brk - this.wristHardMax;
          _fixQ.setFromAxisAngle(_axis, fix);
          this.hand.quaternion.premultiply(_fixQ);
          this.reach.wristBreak = this.wristHardMax;
          this.reach.wristClamped = fix;
          // The forearm roll follows the back of the hand, so re-aim it.
          _up.set(0, 1, 0).applyQuaternion(this.hand.quaternion);
          aimBone(this.forePivot.quaternion, _hp, _up);
        }
      }
    }
    return this;
  }

  /**
   * Collapse the glove's rigid part meshes into one SkinnedMesh per material.
   *
   * MEASURED, tools/drawcensus.mjs at 1366x768 / balanced: the viewmodel pass
   * submitted 115 draw calls a frame and 86 of them were these two hands —
   * 13.5% of every draw call in the game, spent on a pair of gloves. A hand is
   * built as 43 separate meshes because the fingers have to curl: each of the 14
   * joints owns a shell, a pad and a seam, and each of those is its own mesh
   * hanging off its own Object3D so the joint's rotation carries it.
   *
   * They are separate for ARTICULATION, not for shading — every part is rigid
   * with respect to exactly one joint. That is precisely what a skeleton
   * expresses, so the whole hand collapses to one mesh per material (3 draws)
   * with one bone per joint and a single weight of 1.0 on every vertex.
   *
   * PIXEL-EXACT, by construction rather than by tolerance. With a single bone at
   * full weight three's skinning chunk evaluates
   *
   *     transformed = bindMatrixInverse * boneMatrix * bindMatrix * p
   *
   * and the default AttachedBindMode keeps `bindMatrixInverse` equal to the
   * inverse of the mesh's own `matrixWorld` every frame. Baking each part's
   * rest-pose transform relative to the glove into its vertices, and taking the
   * bone inverses at that same instant, makes the product collapse to
   *
   *     bone.matrixWorld * bone.matrixWorld_rest⁻¹ * part.matrixWorld_rest * p
   *
   * which is the position the parented part had. Nothing is welded across parts
   * and no vertex is blended between bones, so there is no smoothing to differ
   * from the hierarchy it replaces.
   *
   * ORDER MATTERS: this must run after `bakeSurfaceMasks` and after every
   * weapon's `bakeContactAO`, because both write vertex attributes through the
   * per-part transform chain that this collapses. `Viewmodel.finalizeHands()`
   * owns that ordering.
   */
  skin() {
    if (this._skinned) return this;
    this.root.updateMatrixWorld(true);

    const skinRoot = this.glove;
    const invRoot = new THREE.Matrix4().copy(skinRoot.matrixWorld).invert();

    // Bone 0 is the glove itself, which carries the palm/knuckle/cuff parts that
    // do not articulate. The rest are exactly the joints the pose code drives.
    const bones = [skinRoot, this.palmArch];
    const boneOf = new Map([[skinRoot, 0], [this.palmArch, 1]]);
    for (const f of this.fingers) {
      for (const j of f.joints) {
        boneOf.set(j, bones.length);
        bones.push(j);
      }
    }
    for (const j of this.thumb.joints) {
      boneOf.set(j, bones.length);
      bones.push(j);
    }

    /** material -> geometries already baked into glove space */
    const groups = new Map();
    const retired = [];
    const m = new THREE.Matrix4();

    skinRoot.traverse((o) => {
      if (!o.isMesh || o.isSkinnedMesh) return;
      let p = o.parent;
      while (p && !boneOf.has(p)) p = p.parent;
      if (!p) return;
      const bone = boneOf.get(p);

      const g = o.geometry.clone();
      // Rest-pose transform of this part, expressed in the glove's space.
      g.applyMatrix4(m.multiplyMatrices(invRoot, o.matrixWorld));

      const n = g.getAttribute('position').count;
      const si = new Float32Array(n * 4);
      const sw = new Float32Array(n * 4);
      for (let i = 0; i < n; i++) {
        si[i * 4] = bone;
        sw[i * 4] = 1;
      }
      g.setAttribute('skinIndex', new THREE.BufferAttribute(si, 4));
      g.setAttribute('skinWeight', new THREE.BufferAttribute(sw, 4));

      let list = groups.get(o.material);
      if (!list) groups.set(o.material, (list = []));
      list.push(g);
      retired.push(o);
    });
    if (!retired.length) return this;

    const skeleton = new THREE.Skeleton(bones);
    // A Skeleton owns a GPU bone texture once rendered.  Unlike the merged
    // geometries it is not reached by Object3D traversal, so retain it for the
    // arm lifecycle and dispose it explicitly in `dispose()`.
    this._skeleton = skeleton;
    const bindMatrix = skinRoot.matrixWorld.clone();
    this._skinMeshes = [];

    for (const [material, list] of groups) {
      const geo = mergeParts(list);
      if (!geo) continue;
      const sm = new THREE.SkinnedMesh(geo, material);
      sm.name = `${this.hand.name}-skin`;
      sm.castShadow = false;
      sm.receiveShadow = true;
      sm.frustumCulled = false;
      sm.bind(skeleton, bindMatrix);
      skinRoot.add(sm);
      this._skinMeshes.push(sm);
    }

    for (const o of retired) {
      o.removeFromParent();
      o.geometry.dispose();
    }
    this._skinned = true;
    return this;
  }

  dispose() {
    this._skeleton?.dispose();
    this._skeleton = null;
    this.root.traverse((o) => {
      if (o.isMesh) o.geometry.dispose();
    });
  }
}

/**
 * Merge parts that already share a material and a space.
 *
 * Deliberately NOT `mergeAll`: that one runs `normalizeAttributes` (which drops
 * everything outside position/normal/uv, i.e. the baked mask colours AND the
 * skin attributes) and then welds coincident vertices, which is a shading change
 * across part boundaries. This only concatenates.
 */
function mergeParts(list) {
  if (!list.length) return null;
  if (list.length === 1) return list[0];

  // mergeGeometries needs every input to carry the same attribute set. The mask
  // bakes add `color` to some parts and not others, so fill the gaps with zeros
  // — which is the value the shader already saw for a part that had none.
  const widths = new Map();
  for (const g of list) {
    for (const [name, a] of Object.entries(g.attributes)) widths.set(name, a.itemSize);
  }
  for (const g of list) {
    const n = g.getAttribute('position').count;
    for (const [name, size] of widths) {
      if (!g.getAttribute(name)) {
        g.setAttribute(name, new THREE.BufferAttribute(new Float32Array(n * size), size));
      }
    }
  }

  // ...and to agree on indexing. Only convert when they disagree.
  const indexed = list.filter((g) => g.getIndex()).length;
  let parts = list;
  if (indexed !== 0 && indexed !== list.length) {
    parts = list.map((g) => (g.getIndex() ? g.toNonIndexed() : g));
  }

  const merged = mergeGeometries(parts, false);
  for (let i = 0; i < list.length; i++) {
    if (parts[i] !== list[i]) parts[i].dispose();
    list[i].dispose();
  }
  return merged;
}

/**
 * Finger curls per pose, in radians per joint (proximal, middle, distal).
 * These are read straight off reference photos of a firing grip: the little
 * finger curls hardest, the index rides the trigger, the thumb wraps high.
 */
export const HAND_POSES = {
  /**
   * CURL IS DISTRIBUTED, NOT DUMPED ON ONE JOINT.
   *
   * The old table put 68-77 degrees on the metacarpophalangeal joint alone and
   * then tapered away (e.g. 77/69/34). Even with the knuckle spheres above —
   * which remove the hard corner at the joint — a single joint carrying three
   * quarters of a right angle while its neighbour carries half of one does not
   * read as a curl, it reads as a hinge. A finger closing on a grip turns
   * through a roughly CONSTANT angle per joint, and that is what makes the
   * knuckle line a smooth arc rather than a dog-leg.
   *
   * So every pose below is authored as a progression with no joint more than
   * ~0.25 rad from its neighbour, at the same total flexion the old table had
   * (which is what actually decides whether the hand closes on the grip).
   */
  /** Firing grip on a pistol grip. */
  grip: {
    /**
     * The three lower fingers wrap ~180 deg of a 31 x 34 mm grip section, which
     * is 2.9-3.1 rad of total flexion. The index is the trigger finger and is
     * driven separately by setTrigger(); the value here is its rest pose, with
     * the slack taken up on the trigger face. Keep the two in step.
     */
    fingers: [
      [0.5, 0.6, 0.46],
      [0.93, 1.03, 0.9],
      [0.97, 1.07, 0.94],
      [0.99, 1.09, 0.96],
    ],
    thumb: [0.46, 0.38],
    thumbBase: [0.15, -1.02, -0.62],
    // A firing grip folds the palm around the grip's back strap.
    arch: 0.38,
  },
  /** Support hand wrapped around a handguard. */
  wrap: {
    fingers: [
      [0.84, 0.94, 0.8],
      [0.9, 1.0, 0.86],
      [0.94, 1.04, 0.9],
      [0.98, 1.08, 0.94],
    ],
    thumb: [0.4, 0.33],
    thumbBase: [0.1, -1.15, -0.35],
    arch: 0.52,
  },
  /**
   * C-clamp on a handguard: the modern support grip, and the only one whose
   * knuckle line turns toward the camera.
   *
   * This is only the SEED. `Arm.fitToCylinder` re-solves it per weapon against
   * that weapon's own handguard radius and stores the result under
   * `clamp:<id>`, so these numbers are what an unfitted clamp falls back to.
   *
   * Summed over the three joints each finger has to sweep the arc from the
   * contact clock angle, round the tube, to the far side: for a 47 mm handguard
   * gripped 14 mm off the surface that is 150-165 deg, i.e. 2.6-2.9 rad total.
   * Anything less and the fingertips stop in mid-air short of the far side.
   */
  clamp: {
    fingers: [
      [0.78, 0.88, 0.76],
      [0.86, 0.96, 0.84],
      [0.85, 0.95, 0.83],
      [0.76, 0.86, 0.74],
    ],
    // Thumb laid ACROSS the top of the handguard rather than forward into space.
    // The thumb root sits at the heel of the palm, which on a C-clamp stands ~50
    // mm off a 47 mm tube (unavoidable: a 98 mm palm tangent to a 23.5 mm radius
    // diverges), so a forward-pointing thumb hangs in mid-air. Aimed at the tube
    // it bridges that gap and closes the silhouette.
    thumb: [0.34, 0.3],
    thumbBase: [0.04, 0.76, -0.05],
    // Re-solved per weapon by fitToCylinder; this is only the seed.
    arch: 0.5,
  },
  /** Two-handed pistol grip: support hand cups the shooting hand. */
  cup: {
    fingers: [
      [0.76, 0.84, 0.7],
      [0.8, 0.88, 0.74],
      [0.84, 0.92, 0.78],
      [0.88, 0.96, 0.82],
    ],
    thumb: [0.3, 0.26],
    thumbBase: [0.0, -1.25, -0.2],
    arch: 0.34,
  },
  /** Open hand: mag grab, charging handle, inspect. */
  open: {
    fingers: [
      [0.26, 0.3, 0.24],
      [0.24, 0.28, 0.22],
      [0.26, 0.3, 0.24],
      [0.3, 0.34, 0.28],
    ],
    thumb: [0.12, 0.1],
    thumbBase: [0.1, -0.8, -0.35],
    // An open hand is a flat hand: the arch is what distinguishes 'reaching
    // for a magazine' from 'holding something'.
    arch: 0.06,
  },
  /** Pinch: holding the charging handle or a magazine by its spine. */
  pinch: {
    fingers: [
      [0.76, 0.84, 0.74],
      [0.8, 0.88, 0.78],
      [0.54, 0.62, 0.52],
      [0.46, 0.54, 0.44],
    ],
    thumb: [0.6, 0.55],
    thumbBase: [0.25, -0.75, -0.7],
    arch: 0.22,
  },
};
